import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import SearchIcon from "lucide-react/dist/esm/icons/search.mjs";
import X from "lucide-react/dist/esm/icons/x.mjs";
import { ApiError, isUnauthorizedApiError, readerApi, validCanonicalItemId } from "./api";
import { archiveTransition } from "./archive";
import { useAutomaticUpdate } from "./useAutomaticUpdate";
import { useCachedValue } from "./useCachedValue";
import { AuthError, currentSession, isStructurallyValidSession, isUsableSession, signInWithPasskey, signOutSession } from "./auth";
import { dailyContext, loadSelectedDailyItems, selectedDailyItemIds } from "./daily";
import { demoApi, demoMode } from "./demo";
import { clearSessionAfterPodcastAuthFailure, useSessionPodcastPreferences } from "./podcastPreferences";
import { clearOfflineReplica, syncOfflineReplica } from "./offlineSync";
import { createSessionIntentCoordinator, invalidateAuthenticatedState, signOutAuthenticatedState, withSameOriginSessionTransitionLock } from "./sessionState";
import {
  cacheAnnotations,
  cacheArticle,
  cacheConfirmedItemState,
  cacheBriefing,
  cacheBriefings,
  cacheCurrentBriefing,
  cacheDaily,
  cacheInboxBrief,
  cacheInboxBriefs,
  cacheList,
  cacheOfflineSession,
  cachePodcastEpisode,
  cachePodcastEpisodes,
  cachedAnnotations,
  cachedArticle,
  cachedBriefing,
  cachedBriefings,
  cachedCurrentBriefing,
  cachedDaily,
  cachedInboxBrief,
  cachedInboxBriefs,
  cachedList,
  cachedOfflineCatalog,
  cachedOfflineSearch,
  cachedOfflineSession,
  cachedPodcastEpisode,
  cachedPodcastEpisodes,
  clearOfflineData,
  deleteCachedItem,
  flushOutbox,
  flushOutboxWithinSessionTransition,
  loadPreferences,
  loadOfflineSyncState,
  queueAnnotationNote,
  queueItemState,
  queueProgress,
  queueReadingSession,
  savePreferences,
  withPendingItemChanges,
} from "./storage";
import type {
  AbridgedDeliveryStatus,
  Annotation,
  AnnotationEnvelope,
  Briefing,
  BriefingEntry,
  BriefingEnvelope,
  DailyEditionEnvelope,
  FeedSource,
  FollowingEnvelope,
  InboxBrief,
  InboxBriefEnvelope,
  InboxBriefPreferences,
  InboxBriefSource,
  ItemListEnvelope,
  LibrarySection,
  OfflineSyncState,
  PodcastEpisode,
  PodcastEpisodeEnvelope,
  PodcastEpisodeSource,
  PodcastPreferencesEnvelope,
  ReaderItem,
  ReaderPreferences,
  ReadingSessionInput,
  SearchItemListEnvelope,
  SearchTruncationReason,
  Session,
} from "./types";
import { Brand } from "./components/Brand";
import { LockedScreen } from "./components/LockedScreen";
import { NavigationRail } from "./components/NavigationRail";
import { ReadingList } from "./components/ReadingList";
import { ReaderPane, type ReaderPaneHandle } from "./components/ReaderPane";
import type { SavePageDraft } from "./components/Overlays";
import { InboxBriefPane } from "./components/InboxBriefPane";
import { PodcastEpisodePane } from "./components/PodcastEpisodePane";
import { BriefingPane } from "./components/BriefingPane";
import { MobilePrimaryNavigation, type MobileDestination } from "./components/MobilePrimaryNavigation";

const AnnotationDrawer = lazy(() => import("./components/Overlays").then((module) => ({ default: module.AnnotationDrawer })));
const SavePageDialog = lazy(() => import("./components/Overlays").then((module) => ({ default: module.SavePageDialog })));
const SettingsPanel = lazy(() => import("./components/Overlays").then((module) => ({ default: module.SettingsPanel })));

const demo = demoMode();
const api = demo ? demoApi : readerApi;
const outboxHandlers = {
  progress: (itemId: string, progress: number) => api.updateState(itemId, { reading_progress: progress }),
  session: (value: ReadingSessionInput) => api.readingSession(value),
  state: (itemId: string, state: { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean }) => api.updateState(itemId, state),
  annotation: (annotationId: string, note: string) => api.updateAnnotation(annotationId, note),
};
const sections: LibrarySection[] = ["library", "starred", "archive"];
const DESKTOP_MEDIA_QUERY = "(min-width: 901px)";

interface LibraryData {
  envelope: ItemListEnvelope;
  daily?: DailyEditionEnvelope;
  offline: boolean;
}

type UpdateScrollSnapshot = { kind: "list" | "reader" | "settings"; top: number; listTop?: number };

export default function App() {
  const { mutate: mutateCache } = useSWRConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [signInBusy, setSignInBusy] = useState(false);
  const [signInError, setSignInError] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Keep drafts in memory through temporary session verification. Components
  // remount with only the verified owner's draft; nothing is written to disk.
  const saveDrafts = useRef(new Map<string, SavePageDraft>());
  const noteDrafts = useRef(new Map<string, Record<string, string>>());
  const [annotationsOpen, setAnnotationsOpen] = useState(false);
  const [annotationBusy, setAnnotationBusy] = useState("");
  const [signOutBusy, setSignOutBusy] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const [followingBusyId, setFollowingBusyId] = useState("");
  const [briefSourceBusyId, setBriefSourceBusyId] = useState("");
  const [briefArchiveBusy, setBriefArchiveBusy] = useState(false);
  const [briefPreferenceBusy, setBriefPreferenceBusy] = useState(false);
  const [podcastSourceBusyId, setPodcastSourceBusyId] = useState("");
  const [podcastArchiveBusy, setPodcastArchiveBusy] = useState(false);
  const [podcastRestoreAllBusy, setPodcastRestoreAllBusy] = useState(false);
  const [podcastArtworkBusy, setPodcastArtworkBusy] = useState(false);
  const [briefingEntryBusyId, setBriefingEntryBusyId] = useState("");
  const [deletingItemKey, setDeletingItemKey] = useState("");
  const [changingItemKey, setChangingItemKey] = useState("");
  const [favoritingItemKey, setFavoritingItemKey] = useState("");
  const [podcastBusy, setPodcastBusy] = useState<"" | "toggle" | "copy" | "apple" | "rotate" | "revoke">("");
  const [notice, setNotice] = useState("");
  const [offlineSyncState, setOfflineSyncState] = useState<OfflineSyncState>();
  const offlineSyncGeneration = useRef(0);
  const [authNeedsRefresh, setAuthNeedsRefresh] = useState(false);
  const [verifiedOwner, setVerifiedOwner] = useState(demo ? "demo" : "");
  const pendingReadingWrites = useRef(new Set<Promise<unknown>>());
  const readingPersistenceFailed = useRef(false);
  const pendingReplays = useRef(new Set<Promise<unknown>>());
  const [replayBusy, setReplayBusy] = useState(false);
  const [preferences, setPreferences] = useState<ReaderPreferences>(() => loadPreferences());
  const [searchValue, setSearchValue] = useState(() => new URLSearchParams(location.search).get("q") || readRetainedSearch(location.state));
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_MEDIA_QUERY).matches);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listScrollPositions = useRef(new Map<string, number>());
  const originFocusRef = useRef("");
  const readerHandleRef = useRef<ReaderPaneHandle>(null);
  const initialUpdateScrollRef = useRef<UpdateScrollSnapshot | undefined>(readUpdateScrollSnapshot(location.state));
  const initialReaderScrollRef = useRef(initialUpdateScrollRef.current?.kind === "reader"
    ? { pathname: location.pathname, top: initialUpdateScrollRef.current.top } : undefined);
  const readingRef = useRef<{ userId: string; itemId: string; startedAt: Date; startProgress: number; latestProgress: number } | null>(null);
  const latestReadingProgress = useRef(0);
  const progressTimer = useRef<number>(0);
  const wasOffline = useRef(!navigator.onLine);
  const requestedBriefDate = useRef("");
  const authEpoch = useRef(0);
  const signInFlight = useRef(false);
  const sessionIntents = useRef(createSessionIntentCoordinator());
  const activeContext = useRef({ generation: 0, key: "", userId: "" });
  const deleteFlights = useRef(new Map<string, Promise<void>>());
  const itemStateFlights = useRef(new Map<string, Promise<void>>());
  const paginationFlights = useRef(new Map<string, Promise<void>>());

  const bootSession = useCachedValue(demo || localSignOutFenced() ? undefined : "session", cachedOfflineSession);
  const { data: observedSession, isLoading: sessionLoading, isValidating: checkingSession, mutate: mutateSession } = useSWR<Session>(
    demo ? "demo-session" : "/auth/session",
    demo ? async () => ({ user_id: "demo", display_name: "Evan", email: "evan@imprint.dibiase.io", expires_at: "" }) : async () => {
      const intent = sessionIntents.current.beginObservation();
      const observed = await sessionIntents.current.observe(intent, async () => {
        if (localSignOutFenced()) {
          await clearOfflineData();
          throw new AuthError(401, "signed_out_locally");
        }
        try {
          if (!navigator.onLine) throw new TypeError("offline");
          const value = await currentSession();
          await cacheOfflineSession(value).catch(() => undefined);
          authEpoch.current++;
          setAuthNeedsRefresh(false);
          setVerifiedOwner(value.user_id);
          return value;
        } catch (error) {
          setVerifiedOwner("");
          if (error instanceof AuthError && error.status === 401) {
            setAuthNeedsRefresh(true);
            const cached = await cachedOfflineSession();
            if (isStructurallyValidSession(cached)) return cached;
            throw error;
          }
          if (!(error instanceof AuthError)) setOnline(false);
          const cached = await cachedOfflineSession();
          if (!isStructurallyValidSession(cached)) throw error;
          if (!isUsableSession(cached)) setAuthNeedsRefresh(true);
          return cached;
        }
      });
      if (!observed.committed) return latestUsableSessionOrThrow(new Error("stale_session_response"));
      return observed.value;
    },
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  // A known local owner can read while the cookie check is slow. Network reads
  // and writes wait for verification, so another account's cookie cannot fill
  // this owner's cache or replay their outbox.
  const bootFallbackAllowed = useRef(true);
  if (observedSession !== undefined) bootFallbackAllowed.current = false;
  const session = observedSession === undefined && sessionLoading && bootFallbackAllowed.current ? bootSession : observedSession;
  const requestAuthEpoch = authEpoch.current;
  const reportExpiredSession = () => {
    if (authEpoch.current === requestAuthEpoch && !localSignOutFenced()) setAuthNeedsRefresh(true);
  };
  const canUseNetwork = online && Boolean(verifiedOwner) && verifiedOwner === session?.user_id && !authNeedsRefresh && !signInBusy && !signOutBusy;
  const networkAllowed = useRef(canUseNetwork);
  networkAllowed.current = canUseNetwork;
  const resumeSession = useRef({ online, retry: false });
  resumeSession.current = { online, retry: !verifiedOwner && !checkingSession && !signInBusy && !signOutBusy };

  const route = useMemo(() => routeState(location.pathname, location.search), [location.pathname, location.search]);
  const settingsOpen = location.pathname === "/settings";
  const briefingHistoryOpen = route.section === "briefings"
    && !route.briefingId
    && (new URLSearchParams(location.search).get("history") === "1"
      || Boolean((location.state as { briefingHistoryOpen?: boolean } | null)?.briefingHistoryOpen));
  const currentBriefingOpen = route.section === "briefings"
    && !route.briefingId
    && !route.briefId
    && !route.podcastEpisodeId
    && !briefingHistoryOpen;
  const briefingReaderOpen = currentBriefingOpen || Boolean(route.briefingId);
  const topLevelAbridged = currentBriefingOpen;
  const readerIntentOpen = Boolean(route.itemId || route.briefId || route.podcastEpisodeId || route.briefingId);
  const activeMobileDestination: MobileDestination = settingsOpen ? "settings" : route.section === "briefings" ? "abridged" : route.section === "search" ? "find" : "library";
  const focus = new URLSearchParams(location.search).get("focus") === "1";
  useEffect(() => {
    if (initialReaderScrollRef.current?.pathname !== location.pathname) initialReaderScrollRef.current = undefined;
  }, [location.pathname]);
  const passiveReaderPreview = desktop && !focus && Boolean(route.itemId)
    && routeStateRecord(location.state).imprintReaderPreview === route.itemId;
  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const changed = () => {
      window.imprintServiceWorkerUpdate?.setSafeToReload(false);
      setDesktop(media.matches);
    };
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, []);
  useLayoutEffect(() => {
    if (!passiveReaderPreview) return;
    const engage = (event: Event) => {
      if (!(event.target instanceof Element) || !event.target.closest(".reader-pane")) return;
      window.imprintServiceWorkerUpdate?.setSafeToReload(false);
      const state = routeStateRecord(location.state);
      delete state.imprintReaderPreview;
      navigate(`${location.pathname}${location.search}`, { replace: true, state });
    };
    // Programmatic heading focus and restored scroll are not reading intent.
    // A deliberate click, touch, wheel or key inside the preview is.
    const events = ["pointerdown", "touchstart", "wheel", "keydown"];
    events.forEach((event) => window.addEventListener(event, engage, { capture: true, passive: true }));
    return () => events.forEach((event) => window.removeEventListener(event, engage, true));
  }, [passiveReaderPreview, location.pathname, location.search, location.state, navigate]);
  useEffect(() => {
    const detailTitle = selectedDocumentTitle(route, settingsOpen, briefingHistoryOpen);
    document.title = detailTitle ? `${detailTitle} — Imprint` : "Imprint";
  }, [briefingHistoryOpen, route, settingsOpen]);
  useEffect(() => {
    if (!readerIntentOpen) return;
    const focusKey = (location.state as { imprintOriginFocus?: string } | null)?.imprintOriginFocus;
    if (focusKey) originFocusRef.current = focusKey;
  }, [location.state, readerIntentOpen]);
  useEffect(() => {
    if (route.section === "search") setSearchValue(route.query);
  }, [route.query, route.section]);
  const searchFocusRequested = Boolean((location.state as { focusSearch?: boolean } | null)?.focusSearch);
  useEffect(() => {
    if (route.section !== "search" || !searchFocusRequested) return;
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, navigate, route.section, searchFocusRequested]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    const updateViewportState = () => {
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      root.style.setProperty("--visual-viewport-height", `${Math.round(height)}px`);
      root.style.setProperty("--visual-viewport-top", `${Math.round(offsetTop)}px`);
      setKeyboardOpen(Boolean(viewport && height < window.innerHeight - 120));
    };
    updateViewportState();
    viewport?.addEventListener("resize", updateViewportState);
    viewport?.addEventListener("scroll", updateViewportState);
    window.addEventListener("resize", updateViewportState);
    return () => {
      viewport?.removeEventListener("resize", updateViewportState);
      viewport?.removeEventListener("scroll", updateViewportState);
      window.removeEventListener("resize", updateViewportState);
      root.style.removeProperty("--visual-viewport-height");
      root.style.removeProperty("--visual-viewport-top");
    };
  }, []);
  useLayoutEffect(() => {
    const snapshot = initialUpdateScrollRef.current;
    if (!snapshot) return;
    let timer = 0;
    let attempts = 0;
    const restore = () => {
      timer = 0;
      const scroller = updateSurfaceScroller(snapshot.kind);
      if (!scroller) {
        attempts += 1;
        if (attempts < 300) timer = window.setTimeout(restore, 50);
        return;
      }
      const maximum = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      scroller.scrollTop = Math.min(snapshot.top, maximum);
      const list = snapshot.listTop === undefined ? null : updateSurfaceScroller("list");
      const listMaximum = list ? Math.max(0, list.scrollHeight - list.clientHeight) : 0;
      if (list) list.scrollTop = Math.min(snapshot.listTop as number, listMaximum);
      attempts += 1;
      if ((maximum + 1 < snapshot.top || (snapshot.listTop !== undefined && (!list || listMaximum + 1 < snapshot.listTop))) && attempts < 300) {
        timer = window.setTimeout(restore, 50);
        return;
      }
      initialUpdateScrollRef.current = undefined;
      const state = routeStateRecord(location.state);
      delete state.imprintUpdateScroll;
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: Object.keys(state).length ? state : null,
      });
    };
    timer = window.setTimeout(restore, 0);
    return () => { if (timer) window.clearTimeout(timer); };
  }, [location.pathname, location.search, navigate]);
  useLayoutEffect(() => {
    if (readerIntentOpen || topLevelAbridged || settingsOpen) return;
    const key = `${location.pathname}${location.search}`;
    const scrollTop = listScrollPositions.current.get(key) ?? 0;
    const frame = window.requestAnimationFrame(() => {
      const scroller = document.querySelector<HTMLElement>(".reading-list__rows");
      if (scroller) {
        scroller.scrollTop = scrollTop;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, readerIntentOpen, settingsOpen, topLevelAbridged]);
  const sessionUserId = session?.user_id;
  const contextKey = `${sessionUserId || ""}\u0000${location.pathname}\u0000${location.search}`;
  useLayoutEffect(() => {
    if (activeContext.current.key === contextKey) return;
    activeContext.current = { generation: activeContext.current.generation + 1, key: contextKey, userId: sessionUserId || "" };
  }, [contextKey, sessionUserId]);
  const listKey = sessionUserId && route.section !== "briefings" ? ["library", sessionUserId, route.section, route.query] : null;
  const searchRequests = useRef(new Set<AbortController>());
  const [searchPaging, setSearchPaging] = useState(false);
  const [searchPageError, setSearchPageError] = useState<string>();
  useLayoutEffect(() => {
    setSearchPaging(false);
    setSearchPageError(undefined);
    return () => {
      for (const controller of searchRequests.current) controller.abort();
      searchRequests.current.clear();
    };
  }, [sessionUserId, route.section, route.query, canUseNetwork, requestAuthEpoch]);
  const { data: liveLibraryData, error: libraryError, isLoading: libraryLoading, isValidating: libraryValidating, mutate: mutateLibrary } = useSWR<LibraryData>(
    listKey,
    async () => {
      const controller = route.section === "search" ? new AbortController() : undefined;
      if (controller) searchRequests.current.add(controller);
      try {
        return await loadLibrary(route.section, route.query, canUseNetwork, sessionUserId as string, reportExpiredSession, controller?.signal);
      } finally {
        if (controller) searchRequests.current.delete(controller);
      }
    },
    { revalidateOnFocus: canUseNetwork, shouldRetryOnError: false },
  );
  const localLibrary = useCachedValue<LibraryData>(
    sessionUserId ? JSON.stringify([sessionUserId, route.section, route.query]) : undefined,
    async () => {
      const catalog = route.section === "search"
        ? await cachedOfflineSearch(sessionUserId as string, route.query)
        : route.section !== "briefings" && route.section !== "daily"
          ? await cachedOfflineCatalog(sessionUserId as string, route.section) : undefined;
      const envelope = catalog || await cachedList(sessionUserId as string, route.section === "search" ? `search:${route.query}` : route.section);
      return envelope ? { envelope, offline: true } : undefined;
    },
  );
  const libraryData = liveLibraryData || localLibrary;
  // Revalidate a cached mount on reconnect; a newly mounted owner/key already
  // starts its own SWR request and must not issue a duplicate search/list GET.
  const previousLibraryConnection = useRef({ owner: sessionUserId, enabled: canUseNetwork });
  useEffect(() => {
    const previous = previousLibraryConnection.current;
    previousLibraryConnection.current = { owner: sessionUserId, enabled: canUseNetwork };
    if (canUseNetwork && !previous.enabled && previous.owner === sessionUserId) void mutateLibrary();
  }, [canUseNetwork, sessionUserId, mutateLibrary]);

  useEffect(() => {
    if (demo || !sessionUserId) return;
    const generation = ++offlineSyncGeneration.current;
    void loadOfflineSyncState(sessionUserId).then((state) => {
      if (generation === offlineSyncGeneration.current) setOfflineSyncState(state);
    }).catch(() => undefined);
    return () => { offlineSyncGeneration.current++; };
  }, [sessionUserId]);

  const runOfflineSync = useCallback(async () => {
    if (demo || !canUseNetwork || !sessionUserId) return;
    const epoch = authEpoch.current;
    try {
      const generation = ++offlineSyncGeneration.current;
      await syncOfflineReplica(sessionUserId, (state) => {
        if (generation === offlineSyncGeneration.current) setOfflineSyncState(state);
      }, () => networkAllowed.current && authEpoch.current === epoch && activeContext.current.userId === sessionUserId);
    } catch (error) {
      if (isUnauthorizedApiError(error) && authEpoch.current === epoch && activeContext.current.userId === sessionUserId) setAuthNeedsRefresh(true);
      // The observer persists and exposes the bounded failure state.
    }
  }, [canUseNetwork, sessionUserId]);
  const { data: briefingEnvelope, error: briefingListError, isLoading: briefingListLoading, mutate: mutateBriefings } = useSWR<BriefingEnvelope>(
    sessionUserId && briefingHistoryOpen ? ["briefings", sessionUserId] : null,
    () => loadBriefings(online, sessionUserId as string),
    { revalidateOnFocus: online },
  );
  const { data: currentUnifiedBriefing, error: currentBriefingError, isLoading: currentBriefingLoading, mutate: mutateCurrentBriefing } = useSWR<Briefing | null>(
    sessionUserId && currentBriefingOpen ? ["briefing-current", sessionUserId] : null,
    () => loadCurrentBriefing(sessionUserId as string),
    { revalidateOnFocus: online, shouldRetryOnError: false },
  );
  useEffect(() => {
    if (demo || !sessionUserId || !currentBriefingOpen) return;
    let active = true;
    void cachedCurrentBriefing(sessionUserId).then((cached) => {
      if (!active || !cached) return;
      void mutateCurrentBriefing((latest) => latest || cached, { revalidate: false });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [currentBriefingOpen, mutateCurrentBriefing, sessionUserId]);
  const { data: abridgedDelivery, error: abridgedDeliveryError, mutate: mutateAbridgedDelivery } = useSWR<AbridgedDeliveryStatus>(
    sessionUserId && currentBriefingOpen && online ? ["abridged-delivery", sessionUserId] : null,
    () => api.abridgedDelivery(),
    {
      revalidateOnFocus: online,
      shouldRetryOnError: false,
      refreshInterval: (latest) => latest && ["ready", "failed", "no_suitable_items"].includes(latest.state) ? 0 : 60_000,
    },
  );
  const { data: routedUnifiedBriefing, error: routedBriefingError, isLoading: routedBriefingLoading, mutate: mutateRoutedBriefing } = useSWR<Briefing | null>(
    sessionUserId && route.section === "briefings" && route.briefingId ? ["briefing", sessionUserId, route.briefingId] : null,
    () => loadBriefing(route.briefingId as string, sessionUserId as string),
    { revalidateOnFocus: online },
  );
  useEffect(() => {
    if (currentBriefingLoading
        || abridgedDelivery?.state !== "ready"
        || currentUnifiedBriefing?.issue_date === abridgedDelivery.issue_date) return;
    void mutateCurrentBriefing();
  }, [abridgedDelivery?.issue_date, abridgedDelivery?.state, currentBriefingLoading, currentUnifiedBriefing?.issue_date, mutateCurrentBriefing]);
  const selectedUnifiedBriefing = currentBriefingOpen ? currentUnifiedBriefing : routedUnifiedBriefing;
  const unifiedBriefingError = currentBriefingOpen ? currentBriefingError : routedBriefingError;
  const unifiedBriefingLoading = currentBriefingOpen ? currentBriefingLoading : routedBriefingLoading;
  const mutateUnifiedBriefing = currentBriefingOpen ? mutateCurrentBriefing : mutateRoutedBriefing;
  const selectedBriefingId = selectedUnifiedBriefing?.id || route.briefingId;

  const itemKey = sessionUserId && route.itemId ? ["item", sessionUserId, route.itemId] : null;
  const { data: liveSelectedItem, error: itemError, isLoading: itemLoading, isValidating: itemRefreshing, mutate: mutateItem } = useSWR<ReaderItem>(
    itemKey,
    () => loadItem(route.itemId as string, sessionUserId as string, canUseNetwork, reportExpiredSession),
    { revalidateOnFocus: canUseNetwork, shouldRetryOnError: false },
  );
  const localItem = useCachedValue(sessionUserId && route.itemId ? `${sessionUserId}:${route.itemId}` : undefined,
    () => cachedArticle(sessionUserId as string, route.itemId as string));
  const selectedItem = liveSelectedItem || (!itemError || !(itemError instanceof ApiError) || itemError.status >= 500 || itemError.status === 401 ? localItem : undefined);
  const previousItemConnection = useRef({ owner: sessionUserId, item: route.itemId, enabled: canUseNetwork });
  useEffect(() => {
    const previous = previousItemConnection.current;
    previousItemConnection.current = { owner: sessionUserId, item: route.itemId, enabled: canUseNetwork };
    if (canUseNetwork && !previous.enabled && previous.owner === sessionUserId && previous.item === route.itemId) void mutateItem();
  }, [canUseNetwork, sessionUserId, route.itemId, mutateItem]);
  useEffect(() => {
    if (!route.itemId || !selectedItem) return;
    const frame = window.requestAnimationFrame(() => readerHandleRef.current?.focusArticleHeading());
    return () => window.cancelAnimationFrame(frame);
  }, [focus, route.itemId, selectedItem?.id]);
  const structuredDetailRouteKey = route.briefingId || route.briefId || route.podcastEpisodeId || "";
  useEffect(() => {
    if (!structuredDetailRouteKey) return;
    let frame = 0;
    let attempts = 0;
    const focusHeading = () => {
      frame = 0;
      const heading = document.querySelector<HTMLElement>("[data-reader-route-heading]");
      if (heading) {
        heading.focus();
        return;
      }
      attempts += 1;
      if (attempts < 120) frame = window.requestAnimationFrame(focusHeading);
    };
    frame = window.requestAnimationFrame(focusHeading);
    return () => { if (frame) window.cancelAnimationFrame(frame); };
  }, [structuredDetailRouteKey]);
  const { data: followingEnvelope, error: followingError, isLoading: followingLoading, mutate: mutateFollowing } = useSWR<FollowingEnvelope>(
    sessionUserId && settingsOpen && canUseNetwork ? ["following", sessionUserId] : null,
    () => api.following(),
    { revalidateOnFocus: online },
  );
  const { data: inboxBriefPreferences, mutate: mutateInboxBriefPreferences } = useSWR<InboxBriefPreferences>(
    null,
    () => api.inboxBriefPreferences(),
    { revalidateOnFocus: online },
  );
  const { data: podcastPreferenceEnvelope, error: podcastPreferenceError, mutate: mutatePodcastPreferences } = useSWR<PodcastPreferencesEnvelope>(
    null,
    () => api.podcastPreferences(),
    { revalidateOnFocus: online },
  );
  const rememberedPodcastPreferenceEnvelope = useSessionPodcastPreferences(
    session?.user_id,
    podcastPreferenceEnvelope,
    isUnauthorizedApiError(podcastPreferenceError),
  );
  const podcastPreferences = rememberedPodcastPreferenceEnvelope?.preferences;
  const podcastFeedUrl = rememberedPodcastPreferenceEnvelope?.feed_url || "";
  const podcastCanManageFeed = Boolean(rememberedPodcastPreferenceEnvelope?.can_manage_feed);
  const briefListStatus = route.section === "archive" ? "archive" : "inbox";
  const { data: inboxBriefEnvelope, error: inboxBriefListError, mutate: mutateInboxBriefs } = useSWR<InboxBriefEnvelope>(
    null,
    () => loadInboxBriefs(briefListStatus, online, sessionUserId as string),
    { revalidateOnFocus: online },
  );
  const { data: selectedBrief, error: briefError, isLoading: briefLoading, mutate: mutateSelectedBrief } = useSWR<InboxBrief>(
    sessionUserId && route.briefId ? ["inbox-brief", sessionUserId, route.briefId] : null,
    () => loadInboxBrief(route.briefId as string, sessionUserId as string),
    { revalidateOnFocus: online },
  );
  const { data: podcastEpisodeEnvelope, error: podcastEpisodeListError, mutate: mutatePodcastEpisodes } = useSWR<PodcastEpisodeEnvelope>(
    null,
    () => loadPodcastEpisodes(briefListStatus, online, sessionUserId as string),
    { revalidateOnFocus: online },
  );
  const { data: selectedPodcastEpisode, error: podcastEpisodeError, isLoading: podcastEpisodeLoading, mutate: mutateSelectedPodcastEpisode } = useSWR<PodcastEpisode>(
    sessionUserId && route.podcastEpisodeId ? ["podcast-episode", sessionUserId, route.podcastEpisodeId] : null,
    () => loadPodcastEpisode(route.podcastEpisodeId as string, sessionUserId as string),
    { revalidateOnFocus: online },
  );

  const annotationKey = sessionUserId && route.itemId && annotationsOpen ? ["annotations", sessionUserId, route.itemId] : null;
  const { data: annotationEnvelope, error: annotationError } = useSWR<AnnotationEnvelope>(
    annotationKey,
    () => loadAnnotations(route.itemId as string, sessionUserId as string, canUseNetwork, reportExpiredSession),
    { revalidateOnFocus: online },
  );

  useLayoutEffect(() => {
    if (!originFocusRef.current) return;
    let frame = 0;
    let attempts = 0;
    const restore = () => {
      frame = 0;
      const key = originFocusRef.current;
      if (!key) return;
      const separator = key.indexOf(":");
      const kind = key.slice(0, separator);
      const value = key.slice(separator + 1);
      const candidates = kind === "list"
        ? [...document.querySelectorAll<HTMLElement>("[data-reading-list-item-id]")]
        : [...document.querySelectorAll<HTMLElement>("[data-reader-origin-key]")];
      const target = candidates.find((candidate) => kind === "list"
        ? candidate.dataset.readingListItemId === value
        : candidate.dataset.readerOriginKey === value)
        || (kind === "list" ? candidates[0] : undefined);
      if (target) {
        target.focus({ preventScroll: true });
        originFocusRef.current = "";
        return;
      }
      attempts += 1;
      if (attempts < 120) frame = window.requestAnimationFrame(restore);
    };
    frame = window.requestAnimationFrame(restore);
    return () => { if (frame) window.cancelAnimationFrame(frame); };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const setConnected = () => setOnline(navigator.onLine);
    const checkOnResume = () => {
      if (document.visibilityState !== "visible") return;
      // A reachable server can temporarily fail verification (e.g. 503) while
      // navigator.onLine remains true; that still deserves a foreground retry.
      if (navigator.onLine && resumeSession.current.online && resumeSession.current.retry && !localSignOutFenced()) void mutateSession();
      setConnected();
    };
    window.addEventListener("online", setConnected);
    window.addEventListener("offline", setConnected);
    window.addEventListener("focus", checkOnResume);
    window.addEventListener("pageshow", checkOnResume);
    document.addEventListener("visibilitychange", checkOnResume);
    return () => {
      window.removeEventListener("online", setConnected);
      window.removeEventListener("offline", setConnected);
      window.removeEventListener("focus", checkOnResume);
      window.removeEventListener("pageshow", checkOnResume);
      document.removeEventListener("visibilitychange", checkOnResume);
    };
  }, [mutateSession]);

  useEffect(() => {
    if (!session) return;
    void import("./components/MarkdownArticle");
    void import("./components/Overlays");
  }, [session]);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setNotice("Offline · cached reading stays available; changes wait for connection");
      return;
    }
    if (wasOffline.current) {
      setNotice("Back online · syncing saved progress");
      void mutateSession();
    }
    wasOffline.current = false;
  }, [online, mutateSession]);

  useEffect(() => {
    if (!canUseNetwork || !session || !liveLibraryData || liveLibraryData.offline) return;
    const ownerId = session.user_id;
    let active = true;
    const replay = flushOutbox(ownerId, {
      progress: (itemId, progress) => api.updateState(itemId, { reading_progress: progress }),
      session: (readingSession) => api.readingSession(readingSession),
      state: outboxHandlers.state,
      annotation: outboxHandlers.annotation,
    }).then(async () => {
      if (!active || activeContext.current.userId !== ownerId) return;
      // Reconnect can fetch the old server list before queued changes arrive.
      // Refresh after replay too, including a reader opened during the flush.
      // Omit replacement data so the visible reading stays intact while loading.
      await mutateCache((key) => Array.isArray(key) && key[1] === ownerId
        && (key[0] === "library" || key[0] === "item"));
      if (active && activeContext.current.userId === ownerId) void runOfflineSync();
    }).catch((error) => {
      if (active && isUnauthorizedApiError(error)) setAuthNeedsRefresh(true);
    });
    pendingReplays.current.add(replay);
    setReplayBusy(true);
    void replay.finally(() => {
      pendingReplays.current.delete(replay);
      setReplayBusy(pendingReplays.current.size > 0);
    });
    return () => { active = false; };
  }, [canUseNetwork, sessionUserId, Boolean(liveLibraryData && !liveLibraryData.offline), mutateCache, runOfflineSync]);

  useEffect(() => {
    if (!selectedItem || !sessionUserId) return;
    void finishReading("item_changed");
    latestReadingProgress.current = selectedItem.reading_progress || 0;
    readingRef.current = document.visibilityState === "visible" ? {
      userId: sessionUserId,
      itemId: selectedItem.id,
      startedAt: new Date(),
      startProgress: selectedItem.reading_progress || 0,
      latestProgress: selectedItem.reading_progress || 0,
    } : null;
    return () => { void finishReading("reader_exit"); };
  }, [selectedItem?.id, sessionUserId]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") {
        void finishReading("visibility_hidden");
      } else if (!readingRef.current && selectedItem && sessionUserId) {
        readingRef.current = {
          userId: sessionUserId,
          itemId: selectedItem.id,
          startedAt: new Date(),
          startProgress: latestReadingProgress.current,
          latestProgress: latestReadingProgress.current,
        };
      }
    };
    const onPageHide = () => { void finishReading("page_exit"); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [online, selectedItem?.id, sessionUserId]);

  useEffect(() => {
    const channel = new BroadcastChannel("imprint-session");
    channel.onmessage = (event) => {
      if (event.data === "session-changed") {
        authEpoch.current++;
        networkAllowed.current = false;
        setVerifiedOwner("");
        void mutateSession(undefined, { revalidate: true });
        return;
      }
      if (event.data !== "signed-out") return;
      authEpoch.current++;
      networkAllowed.current = false;
      saveDrafts.current.clear();
      noteDrafts.current.clear();
      setVerifiedOwner("");
      window.clearTimeout(progressTimer.current);
      progressTimer.current = 0;
      readingRef.current = null;
      const intent = sessionIntents.current.beginTransition();
      void sessionIntents.current.transition(intent, () => invalidateAuthenticatedState({
        invalidateSession: () => mutateSession(undefined, { revalidate: false }),
        clearOffline: clearOfflineData,
      }));
    };
    return () => channel.close();
  }, [mutateSession]);

  const authenticationFailed = [briefingListError, unifiedBriefingError, abridgedDeliveryError, followingError, inboxBriefListError, briefError, podcastPreferenceError, podcastEpisodeListError, podcastEpisodeError].some(isUnauthorizedApiError);
  useEffect(() => {
    if (authenticationFailed) setAuthNeedsRefresh(true);
  }, [authenticationFailed]);
  useEffect(() => {
    if (demo || !session) return;
    let timer = 0;
    const check = () => {
      const remaining = Date.parse(session.expires_at) - Date.now();
      if (remaining > 0) { timer = window.setTimeout(check, Math.min(remaining, 2_147_483_647)); return; }
      setAuthNeedsRefresh(true);
    };
    check();
    return () => window.clearTimeout(timer);
  }, [session]);

  useEffect(() => {
    if (!online || !inboxBriefPreferences?.enabled || route.section !== "inbox") return;
    const issueDate = dailyContext().issueDate;
    if (requestedBriefDate.current === issueDate || inboxBriefEnvelope?.briefs.some((brief) => brief.issue_date === issueDate)) return;
    requestedBriefDate.current = issueDate;
    void api.requestInboxBrief(issueDate).then(() => mutateInboxBriefs()).catch(() => undefined);
  }, [inboxBriefEnvelope?.briefs, inboxBriefPreferences?.enabled, mutateInboxBriefs, online, route.section]);

  const finishReading = useCallback((reason: string, outboxFlush = flushOutbox, allowFlush = networkAllowed.current) => {
    const writing = (async () => {
      const reading = readingRef.current;
      if (!reading) return;
      readingRef.current = null;
      window.clearTimeout(progressTimer.current);
      progressTimer.current = 0;
      const endedAt = new Date();
      const seconds = Math.max(0, Math.round((endedAt.getTime() - reading.startedAt.getTime()) / 1000));
      const progressDelta = Math.abs(reading.latestProgress - reading.startProgress);
      if (progressDelta >= 0.01) await queueProgress(reading.userId, reading.itemId, reading.latestProgress);
      if (seconds <= 0 || (seconds < 5 && progressDelta < 0.01)) return;
      const payload: ReadingSessionInput = {
        item_id: reading.itemId,
        started_at: reading.startedAt.toISOString(),
        ended_at: endedAt.toISOString(),
        seconds_read: seconds,
        progress_start: reading.startProgress,
        progress_end: reading.latestProgress,
        source: "web",
        metadata: { reason },
      };
      await queueReadingSession(reading.userId, payload);
      if (allowFlush) await outboxFlush(reading.userId, outboxHandlers).catch(() => undefined);
    })();
    pendingReadingWrites.current.add(writing);
    void writing.catch(() => { readingPersistenceFailed.current = true; })
      .finally(() => pendingReadingWrites.current.delete(writing));
    return writing;
  }, []);

  const handleProgress = useCallback((progress: number) => {
    if (!selectedItem) return;
    latestReadingProgress.current = progress;
    if (readingRef.current) readingRef.current.latestProgress = progress;
    if (Math.abs(progress - (selectedItem.reading_progress || 0)) < 0.01) return;
    window.clearTimeout(progressTimer.current);
    progressTimer.current = window.setTimeout(() => {
      if (!session) return;
      const epoch = authEpoch.current;
      const writing = queueProgress(session.user_id, selectedItem.id, progress);
      pendingReadingWrites.current.add(writing);
      void writing.catch((error) => { readingPersistenceFailed.current = true; throw error; })
        .finally(() => pendingReadingWrites.current.delete(writing)).then(async () => {
        if (networkAllowed.current) await flushOutbox(session.user_id, outboxHandlers);
      }).catch((error) => { if (authEpoch.current === epoch && isUnauthorizedApiError(error)) setAuthNeedsRefresh(true); });
    }, 1500);
  }, [canUseNetwork, selectedItem, session]);

  useAutomaticUpdate(
    (readerIntentOpen && !passiveReaderPreview) || settingsOpen || saveOpen || annotationsOpen || signInBusy || signOutBusy
      || replayBusy || saveBusy || Boolean(annotationBusy || deletingItemKey || changingItemKey || favoritingItemKey)
      || (route.section === "search" && searchValue !== route.query),
    `${location.pathname}${location.search}`,
    async () => {
      // A desktop preview may be displayed while the user browses the list.
      // Checkpoint it locally before reload, without waiting for the network.
      if (passiveReaderPreview && readingRef.current) {
        const reading = readingRef.current;
        const checkpoint = finishReading("app_update", flushOutbox, false);
        readingRef.current = { ...reading, startedAt: new Date(), startProgress: reading.latestProgress };
        await checkpoint;
      }
      await Promise.all([...pendingReadingWrites.current]);
      if (readingPersistenceFailed.current) throw new Error("reading_checkpoint_failed");
      const snapshot = captureUpdateSurfaceScroll();
      // Preserve React Router's own history bookkeeping without moving focus.
      window.history.replaceState({ ...window.history.state, usr: { ...routeStateRecord(location.state), imprintUpdateScroll: snapshot, imprintSearchValue: searchValue } }, "");
    },
  );

  const firstPodcastEpisodeId = podcastEpisodeEnvelope?.episodes[0]?.id;
  const firstBriefId = inboxBriefEnvelope?.briefs[0]?.id;
  const firstItemId = libraryData?.envelope.items[0]?.id;
  useEffect(() => {
    if (route.itemId || route.briefId || route.podcastEpisodeId || route.briefingId || currentBriefingOpen || (!firstPodcastEpisodeId && !firstBriefId && !firstItemId) || !window.matchMedia(DESKTOP_MEDIA_QUERY).matches || location.pathname === "/settings") return;
    const destination = firstPodcastEpisodeId
      ? podcastEpisodePath(firstPodcastEpisodeId, route.section)
      : firstBriefId
        ? briefPath(firstBriefId, route.section)
        : `/read/${encodeURIComponent(firstItemId as string)}?section=${route.section}${route.query ? `&q=${encodeURIComponent(route.query)}` : ""}`;
    navigate(destination, { replace: true, state: { imprintReaderPreview: firstItemId } });
  }, [firstBriefId, firstItemId, firstPodcastEpisodeId, location.pathname, navigate, route.briefId, route.itemId, route.podcastEpisodeId, route.query, route.section]);

  if (location.pathname === "/") return <Navigate to="/library" replace />;
  if (/^\/(?:abridged|briefings|podcast|podcast-episodes|daily)(?:\/|$)/.test(location.pathname)) return <Navigate to="/library" replace />;
  if (sessionLoading && briefingReaderOpen) {
    return (
      <main className="app-shell app-shell--abridged app-shell--session-loading">
        <header className="global-header global-header--abridged">
          <div className="global-header__mobile-brand"><Brand compact /></div>
          <button className="abridged-history-command" type="button" disabled>Past editions</button>
        </header>
        <BriefingPane root briefing={undefined} delivery={undefined} loading error="" online={online} preferences={preferences} busyItemId="" onBack={openBriefingHistory} onReadOriginal={() => undefined} />
        <MobilePrimaryNavigation active="abridged" onSelect={selectMobileDestination} />
      </main>
    );
  }
  if (sessionLoading && !session) return <main className="app-loading"><Brand /><p>Opening your library…</p></main>;
  if (!session) {
    return <LockedScreen busy={signInBusy} offline={!online} error={signInError} onSignIn={() => void handleSignIn()} />;
  }

  const items = libraryData?.envelope.items || [];
  const inboxBriefs = inboxBriefEnvelope?.briefs || [];
  const podcastEpisodes: PodcastEpisode[] = [];
  const daily = libraryData?.daily;
  const listLoading = route.section === "briefings" ? briefingListLoading
    : route.section === "search" ? Boolean(route.query) && (libraryLoading || searchPaging || libraryValidating) : libraryLoading;
  const listCount = items.length + inboxBriefs.length + podcastEpisodes.length;
  const libraryScope = route.section === "starred" || route.section === "archive" ? route.section : "library";
  const listTitle = route.section === "briefings" ? "Past editions" : route.section === "daily" ? daily?.issue?.title || "Daily" : route.section === "search" ? "Find" : "Library";
  const listSubtitle = route.section === "daily"
    ? daily?.issue?.subtitle || dailyStatus(daily)
    : route.section === "briefings"
      ? "Every previous edition, kept as a receipt"
    : route.section === "search"
      ? route.query
        ? listLoading && !libraryData ? `Searching for “${route.query}”…` : `${items.length} match${items.length === 1 ? "" : "es"}${libraryData?.offline ? " in cached reads" : libraryData?.envelope.search_status && libraryData.envelope.search_status !== "complete" ? " so far" : ""} for “${route.query}”`
        : "Titles, authors, sites, or topics"
      : listLoading && !libraryData
        ? "Opening saved reads…"
        : route.section === "starred"
          ? `${listCount} starred`
          : route.section === "archive"
            ? `${listCount} archived`
            : `${listCount} saved${libraryData?.offline ? " · Cached on this device" : ""}`;
  const incompleteSearchNotice = route.section === "search" && route.query ? searchIncompleteNotice(libraryData?.envelope, canUseNetwork, Boolean(libraryData?.offline), listLoading) : undefined;
  const retrySearch = route.section === "search" && canUseNetwork && (Boolean(libraryData?.offline)
    || Boolean(libraryData?.envelope.search_truncated && libraryData.envelope.search_truncation_reasons?.includes("artifact_invalid")));
  if (focus && route.itemId) {
    return (
      <>
        <main className={`focus-reader-main${authNeedsRefresh ? " has-session-status" : ""}`}>
          {authNeedsRefresh ? sessionStatus() : null}
          <ReaderPane initialScrollTop={initialReaderScrollRef.current?.pathname === location.pathname ? initialReaderScrollRef.current.top : undefined} onInitialScrollRestored={() => { initialReaderScrollRef.current = undefined; }} item={selectedItem} loading={itemLoading} refreshing={itemRefreshing} error={friendlyError(itemError)} focus online={online} deleteBusy={deletingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} favoriteBusy={favoritingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} stateBusy={changingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} backLabel={titleCase(route.section)} preferences={preferences} annotationsCount={annotationEnvelope?.annotations.length || 0} onBack={() => returnFromDetail(sectionPath(route.section, route.query))} onFocus={() => setFocus(false)} onFavorite={() => void toggleFavorite()} onArchive={() => void archiveItem()} onDelete={() => void deleteItem()} onAnnotations={() => setAnnotationsOpen(true)} onPreferences={updatePreferences} onProgress={handleProgress} onRetry={() => void mutateItem()} readerHandleRef={readerHandleRef} />
        </main>
        <Suspense fallback={null}>{annotationsOpen ? <AnnotationDrawer key={`${sessionUserId}:${route.itemId}`} initialDrafts={noteDrafts.current.get(`${sessionUserId}:${route.itemId}`)} onDraftChange={(id, note) => { const key = `${sessionUserId}:${route.itemId}`; noteDrafts.current.set(key, { ...noteDrafts.current.get(key), [id]: note }); }} annotations={annotationEnvelope?.annotations || []} online={online} busyId={annotationBusy} onClose={() => { noteDrafts.current.delete(`${sessionUserId}:${route.itemId}`); setAnnotationsOpen(false); }} onSave={saveAnnotation} /> : null}</Suspense>
        <NoticeRegion notice={notice} onDismiss={() => setNotice("")} />
      </>
    );
  }

  return (
    <>
      <main className={`app-shell${readerIntentOpen ? " app-shell--reader-open" : ""}${topLevelAbridged ? " app-shell--abridged" : ""}${settingsOpen ? " app-shell--settings" : ""}${keyboardOpen ? " app-shell--keyboard-open" : ""}${authNeedsRefresh ? " has-session-status" : ""}`}>
        {authNeedsRefresh ? sessionStatus() : null}
        <NavigationRail active={route.section} counts={{ [route.section === "search" ? "queue" : route.section]: items.length }} online={online} onSelect={(section) => navigate(sectionPath(section))} onSave={() => setSaveOpen(true)} onSettings={() => navigate("/settings")} />
        <header className={`global-header${route.section === "search" ? " global-header--search" : ""}${topLevelAbridged ? " global-header--abridged" : ""}`}>
          <div className="global-header__mobile-brand"><Brand compact /></div>
          <form className={`global-search${route.section === "search" ? " global-search--active" : ""}`} onSubmit={(event) => { event.preventDefault(); submitSearch(); }}>
            <SearchIcon aria-hidden="true" />
            <label className="sr-only" htmlFor="global-search">Search Imprint</label>
            <input ref={searchInputRef} id="global-search" type="search" enterKeyHint="search" value={searchValue} maxLength={512} onChange={(event) => changeSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); submitSearch(); } }} placeholder="Titles, authors, sites, or topics" />
            {searchValue ? <button className="global-search__clear" type="button" aria-label="Clear search" onClick={() => { changeSearch(""); searchInputRef.current?.focus(); }}><X aria-hidden="true" /></button> : null}
            <button className="global-search__submit" type="submit" aria-label="Search">Search</button>
          </form>
          {topLevelAbridged ? <button className="abridged-history-command" type="button" onClick={openBriefingHistory}>Past editions</button> : null}
        </header>
        <ReadingList title={listTitle} subtitle={listSubtitle} section={route.section} items={items} briefs={inboxBriefs} podcastEpisodes={podcastEpisodes} briefings={briefingEnvelope?.briefings || []} selectedId={selectedBriefingId || route.podcastEpisodeId || route.briefId || route.itemId} entries={daily?.entries} coverArtwork={daily?.issue?.artwork} loading={listLoading} error={(route.section === "search" ? searchPageError : undefined) || friendlyError(briefingListError) || friendlyError(libraryError) || friendlyError(inboxBriefListError) || friendlyError(podcastEpisodeListError)} incompleteNotice={incompleteSearchNotice} retrySearch={retrySearch} searchQuery={route.query} offline={Boolean(libraryData?.offline)} hasMore={canUseNetwork && !(route.section === "search" && libraryData?.offline) && (route.section === "briefings" ? Boolean(briefingEnvelope?.has_more) : Boolean(libraryData?.envelope.has_more))} libraryControls={route.section === "library" || route.section === "starred" || route.section === "archive" ? { scope: libraryScope, online, onScope: (scope) => navigate(sectionPath(scope), { replace: true }), onSave: () => setSaveOpen(true) } : undefined} onOpen={(item) => openDetail(readerPath(item.id, route.section, route.query))} onOpenBrief={(brief) => openDetail(briefPath(brief.id, brief.status))} onOpenPodcastEpisode={(episode) => openDetail(podcastEpisodePath(episode.id, episode.status))} onOpenBriefing={(briefing) => openDetail(`/abridged/${encodeURIComponent(briefing.id)}`)} onLoadMore={() => void loadMore()} onRetry={() => { if (route.section === "briefings") void mutateBriefings(); else { setSearchPageError(undefined); void mutateLibrary(); } }} />
        {briefingReaderOpen ? (
          <BriefingPane root={topLevelAbridged} briefing={selectedUnifiedBriefing} delivery={currentBriefingOpen ? abridgedDelivery : undefined} loading={unifiedBriefingLoading} error={friendlyError(unifiedBriefingError) || friendlyError(abridgedDeliveryError)} online={online} preferences={preferences} busyItemId={briefingEntryBusyId} onBack={topLevelAbridged ? openBriefingHistory : () => returnFromDetail("/abridged?history=1")} onReadOriginal={(entry) => void readBriefingOriginal(entry)} onRetry={() => { void mutateUnifiedBriefing(); if (currentBriefingOpen) void mutateAbridgedDelivery(); }} />
        ) : route.podcastEpisodeId ? (
          <PodcastEpisodePane episode={selectedPodcastEpisode} loading={podcastEpisodeLoading} error={friendlyError(podcastEpisodeError)} online={online} preferences={preferences} busySource={podcastSourceBusyId} archiveBusy={podcastArchiveBusy} restoreAllBusy={podcastRestoreAllBusy} artworkBusy={podcastArtworkBusy} onBack={() => returnFromDetail(sectionPath(selectedPodcastEpisode?.status || route.section))} onOpenSource={(source) => openDetail(readerPath(source.item_id, source.state === "archived" ? "archive" : source.original_status))} onKeep={(source) => void keepPodcastSource(source)} onRestore={(source) => void restorePodcastSource(source)} onArchive={(itemIds) => void archivePodcastSources(itemIds)} onRestoreAll={() => void restoreAllPodcastSources()} onGenerateArtwork={() => void generatePodcastArtwork()} onRetry={() => void mutateSelectedPodcastEpisode()} />
        ) : route.briefId ? (
          <InboxBriefPane brief={selectedBrief} loading={briefLoading} error={friendlyError(briefError)} online={online} preferences={preferences} busySource={briefSourceBusyId} archiveBusy={briefArchiveBusy} onBack={() => returnFromDetail(sectionPath(selectedBrief?.status || route.section))} onOpenSource={(source) => openDetail(readerPath(source.item_id, source.disposition === "archived" ? "archive" : "inbox"))} onKeep={(source) => void keepBriefSource(source)} onRestore={(source) => void restoreBriefSource(source)} onArchive={() => void archiveSelectedBrief()} onRetry={() => void mutateSelectedBrief()} />
        ) : (
          <ReaderPane initialScrollTop={initialReaderScrollRef.current?.pathname === location.pathname ? initialReaderScrollRef.current.top : undefined} onInitialScrollRestored={() => { initialReaderScrollRef.current = undefined; }} item={selectedItem} loading={itemLoading} refreshing={itemRefreshing} error={friendlyError(itemError)} focus={false} online={online} deleteBusy={deletingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} favoriteBusy={favoritingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} stateBusy={changingItemKey === `${sessionUserId}\u0000${selectedItem?.id || ""}`} backLabel={titleCase(route.section)} preferences={preferences} annotationsCount={annotationEnvelope?.annotations.length || 0} onBack={() => returnFromDetail(sectionPath(route.section, route.query))} onFocus={() => setFocus(true)} onFavorite={() => void toggleFavorite()} onArchive={() => void archiveItem()} onDelete={() => void deleteItem()} onAnnotations={() => setAnnotationsOpen(true)} onPreferences={updatePreferences} onProgress={handleProgress} onRetry={() => void mutateItem()} readerHandleRef={readerHandleRef} />
        )}
        <Suspense fallback={null}>
          {settingsOpen ? <SettingsPanel page session={session} preferences={preferences} inboxBriefEnabled={Boolean(inboxBriefPreferences?.enabled)} inboxBriefBusy={briefPreferenceBusy} podcastPreferences={podcastPreferences} podcastCanManageFeed={podcastCanManageFeed} podcastBusy={podcastBusy} podcastFeedUrl={podcastFeedUrl} following={followingEnvelope?.feed_sources || []} followingLoading={online && followingLoading} followingError={online ? friendlyError(followingError) : "Following is available when this device is online."} followingBusyId={followingBusyId} offlineCount={offlineSyncState?.body_count || 0} offlineSync={offlineSyncState} online={online} reauthRequired={authNeedsRefresh} signOutBusy={signOutBusy} signOutError={signOutError} onPreferences={updatePreferences} onInboxBrief={updateInboxBriefPreference} onPodcastEnabled={updatePodcastPreference} onPodcastCopy={copyPodcastFeedUrl} onPodcastApple={subscribePodcastInApplePodcasts} onPodcastRotate={rotatePodcastFeed} onPodcastRevoke={revokePodcastFeed} onUnfollow={unfollowSource} onSync={runOfflineSync} onReauthenticate={handleSignIn} onClear={clearOfflineDataWithNotice} onSignOut={handleSignOut} onClose={() => navigate(sectionPath(route.section === "search" ? "library" : route.section))} /> : null}
        </Suspense>
        {!readerIntentOpen ? <MobilePrimaryNavigation active={activeMobileDestination} onSelect={selectMobileDestination} /> : null}
      </main>
      <NoticeRegion notice={notice} onDismiss={() => setNotice("")} />
      <Suspense fallback={null}>
        {saveOpen ? <SavePageDialog key={sessionUserId} initialDraft={saveDrafts.current.get(sessionUserId as string)} onDraftChange={(draft) => saveDrafts.current.set(sessionUserId as string, draft)} busy={saveBusy} error={saveError} online={online} onClose={closeSavePage} onSave={savePage} /> : null}
        {annotationsOpen && selectedItem ? <AnnotationDrawer key={`${sessionUserId}:${route.itemId}`} initialDrafts={noteDrafts.current.get(`${sessionUserId}:${route.itemId}`)} onDraftChange={(id, note) => { const key = `${sessionUserId}:${route.itemId}`; noteDrafts.current.set(key, { ...noteDrafts.current.get(key), [id]: note }); }} annotations={annotationEnvelope?.annotations || []} online={online} busyId={annotationBusy} onClose={() => { noteDrafts.current.delete(`${sessionUserId}:${route.itemId}`); setAnnotationsOpen(false); }} onSave={saveAnnotation} /> : null}
      </Suspense>
    </>
  );

  function closeSavePage() {
    if (sessionUserId) saveDrafts.current.delete(sessionUserId);
    setSaveOpen(false);
  }

  async function handleSignIn() {
    if (signInFlight.current) return;
    signInFlight.current = true;
    const intent = sessionIntents.current.beginTransition();
    setSignInBusy(true);
    setSignInError("");
    try {
      await sessionIntents.current.transition(intent, async () => {
        const value = await signInWithPasskey();
        clearLocalSignOutFence();
        await cacheOfflineSession(value).catch(() => undefined);
        authEpoch.current++;
        setAuthNeedsRefresh(false);
        setVerifiedOwner(value.user_id);
        await mutateSession(value, { revalidate: false });
        try {
          const channel = new BroadcastChannel("imprint-session");
          try { channel.postMessage("session-changed"); } finally { channel.close(); }
        } catch { /* A successful passkey sign-in remains valid if tab messaging fails. */ }
      });
    }
    catch (error) { if (sessionIntents.current.isCurrent(intent)) setSignInError(error instanceof Error ? error.message : "Passkey sign-in failed."); }
    finally { signInFlight.current = false; if (sessionIntents.current.isCurrent(intent)) setSignInBusy(false); }
  }

  function openBriefingHistory() {
    navigate("/abridged?history=1", { state: { briefingHistoryOpen: true } });
  }

  function submitSearch() {
    navigate(sectionPath("search", searchValue.trim()), { replace: route.section === "search" });
  }

  function changeSearch(value: string) {
    setSearchValue(value);
    // Both our clear button and the browser's native search-field clear must
    // reset the submitted query, not leave results for an invisible query.
    if (!value && route.section === "search") navigate("/search", { replace: true });
  }

  function selectMobileDestination(destination: MobileDestination) {
    if (destination === "find") {
      if (route.section === "search") {
        searchInputRef.current?.focus({ preventScroll: true });
        return;
      }
      rememberListScroll();
      navigate(sectionPath("search", searchValue), { replace: true, state: { focusSearch: true } });
      return;
    }
    rememberListScroll();
    const path = destination === "abridged" ? "/abridged" : destination === "library" ? "/library" : "/settings";
    navigate(path, { replace: true });
  }

  function rememberListScroll() {
    const scroller = document.querySelector<HTMLElement>(".reading-list__rows");
    if (scroller) listScrollPositions.current.set(`${location.pathname}${location.search}`, scroller.scrollTop);
  }

  function openDetail(destination: string, explicitFocusKey = "") {
    rememberListScroll();
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const listKey = active?.closest<HTMLElement>("[data-reading-list-item-id]")?.dataset.readingListItemId;
    const readerKey = active?.closest<HTMLElement>("[data-reader-origin-key]")?.dataset.readerOriginKey;
    const focusKey = explicitFocusKey || (listKey ? `list:${listKey}` : readerKey ? `reader:${readerKey}` : "");
    if (focusKey) originFocusRef.current = focusKey;
    navigate(destination, { state: { imprintOrigin: `${location.pathname}${location.search}`, imprintOriginFocus: focusKey } });
  }

  function returnFromDetail(fallback: string) {
    const state = location.state as { imprintOrigin?: string; imprintOriginFocus?: string } | null;
    if (state?.imprintOriginFocus) originFocusRef.current = state.imprintOriginFocus;
    if (state?.imprintOrigin) navigate(-1);
    else navigate(fallback, { replace: true });
  }

  function sessionStatus() {
    return <aside className="session-status" aria-label="Account status">
      <div><strong>Signed out</strong><span>Reading saved on this device</span></div>
      <button type="button" disabled={signInBusy || !online} onClick={() => void handleSignIn()}>{signInBusy ? "Opening passkey…" : online ? "Sign in with passkey" : "Sign in when online"}</button>
      {signInError ? <p role="alert">{signInError}</p> : null}
    </aside>;
  }

  async function savePage(url: string, title: string, articleText = "") {
    if (!canUseNetwork) { setSaveError(authNeedsRefresh ? "Sign in with your passkey to save a new page." : "Saving is available when this device is online."); return; }
    setSaveBusy(true);
    setSaveError("");
    try {
      const result = await api.save(url, title, articleText);
      await mutateLibrary();
      if (result.acquisition?.state === "source_blocked" || result.acquisition?.state === "unavailable") {
        setSaveError(result.acquisition.state === "source_blocked"
          ? "URL saved, but the site blocked automated access. Open the page and paste its article text below, or use the Imprint browser extension."
          : "URL saved, but Imprint could not download the article. Paste its text below, or use the Imprint browser extension.");
        return;
      }
      closeSavePage();
      if (result.item) {
        setNotice("Saved to Library · preparing a reading copy");
        openDetail(`/read/${encodeURIComponent(result.item.id)}?section=library`);
      } else {
        setNotice(result.materialization.deferred_reason === "source_identity_runtime_staged"
          || result.materialization.deferred_reason === "source_identity_runtime_paused"
          ? "Save received · Library import is currently blocked"
          : "Save received · not yet added to Library");
      }
    } catch (error) { setSaveError(friendlyError(error) || "Could not save this page."); }
    finally { setSaveBusy(false); }
  }

  async function toggleFavorite() {
    if (!selectedItem || !sessionUserId) return;
    const item = selectedItem;
    const ownerId = sessionUserId;
    const flightKey = `${ownerId}\u0000${item.id}`;
    if (itemStateFlights.current.has(flightKey)) return itemStateFlights.current.get(flightKey);
    if (deleteFlights.current.has(flightKey)) return;
    const context = { ...activeContext.current };
    const state = { is_favorite: !item.is_favorite };
    let local = !canUseNetwork;
    const operation = (async () => {
      setFavoritingItemKey(flightKey);
      try {
        const confirmed = await withSameOriginSessionTransitionLock(async () => {
          const current = demo
            ? sameActiveContext(activeContext.current, context)
            : await itemMutationPreflightIsCurrent(context, () => activeContext.current, ownerId);
          if (!current) return undefined;
          if (local) {
            await queueItemState(ownerId, item.id, state);
            return state;
          }
          let result: ReaderItem;
          try { result = await api.updateState(item.id, state); }
          catch (error) {
            if (!isUnauthorizedApiError(error)) throw error;
            reportExpiredSession();
            local = true;
            await queueItemState(ownerId, item.id, state);
            return state;
          }
          const acknowledged = { is_favorite: result.is_favorite ?? state.is_favorite };
          if (!demo) await cacheConfirmedItemState(ownerId, item.id, acknowledged).catch(() => undefined);
          return acknowledged;
        });
        if (!confirmed) return;
        // A bound SWR mutator follows the latest route. Address the original
        // item explicitly, and change only the acknowledged field on its latest copy.
        await mutateCache<ReaderItem>(["item", ownerId, item.id], (current) => current
          ? { ...current, ...confirmed } : current, { revalidate: false });
        void mutateCache((key) => Array.isArray(key) && key[0] === "library" && key[1] === ownerId,
          undefined, { revalidate: true }).catch(() => undefined);
        if (!sameActiveContext(activeContext.current, context)) return;
        setNotice(confirmed.is_favorite
          ? local ? "Added to Starred · waiting to sync" : "Added to Starred"
          : local ? "Removed from Starred · waiting to sync" : "Removed from Starred");
      } catch (error) {
        if (sameActiveContext(activeContext.current, context)) setNotice(friendlyError(error) || "Could not update this favorite.");
      } finally {
        setFavoritingItemKey((current) => current === flightKey ? "" : current);
      }
    })();
    itemStateFlights.current.set(flightKey, operation);
    try { await operation; }
    finally { if (itemStateFlights.current.get(flightKey) === operation) itemStateFlights.current.delete(flightKey); }
  }

  async function readBriefingOriginal(entry: BriefingEntry) {
    if (!selectedUnifiedBriefing || (!online && entry.state === "archived")) return;
    const originFocusKey = `reader:abridged:${entry.item_id}`;
    setBriefingEntryBusyId(entry.item_id);
    try {
      if (entry.state === "archived") {
        const briefing = await api.restoreBriefingEntry(selectedUnifiedBriefing.id, entry.item_id);
        await Promise.all([mutateUnifiedBriefing(briefing, { revalidate: false }), mutateBriefings(), mutateLibrary()]);
        persistOffline(cacheBriefing(sessionUserId as string, briefing));
      }
      openDetail(readerPath(entry.item_id, "library"), originFocusKey);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not open the original reading.");
    } finally {
      setBriefingEntryBusyId("");
    }
  }

  async function archiveItem() {
    if (!selectedItem || !sessionUserId) return;
    const item = selectedItem;
    const ownerId = sessionUserId;
    const flightKey = `${ownerId}\u0000${item.id}`;
    if (itemStateFlights.current.has(flightKey)) return itemStateFlights.current.get(flightKey);
    if (deleteFlights.current.has(flightKey)) return;
    const context = { ...activeContext.current };
    const restoring = item.status === "archive";
    const state = { status: restoring ? "queue" as const : "archive" as const };
    let local = !canUseNetwork;
    const removesFromSurface = route.section !== "search"
      && (restoring ? route.section === "archive" : route.section !== "archive");
    const transition = archiveTransition(items, item.id);
    const isMobile = !window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    const destination = isMobile || !transition.adjacentItem
      ? sectionPath(route.section, route.query)
      : readerPath(transition.adjacentItem.id, route.section, route.query);
    const operation = (async () => {
      setChangingItemKey(flightKey);
      try {
        const changedItem = await withSameOriginSessionTransitionLock(async () => {
          const current = demo
            ? sameActiveContext(activeContext.current, context)
            : await itemMutationPreflightIsCurrent(context, () => activeContext.current, ownerId);
          if (!current) return undefined;
          if (local) {
            await queueItemState(ownerId, item.id, state);
            return { ...item, ...state };
          }
          let result: ReaderItem;
          try { result = await api.updateState(item.id, state); }
          catch (error) {
            if (!isUnauthorizedApiError(error)) throw error;
            reportExpiredSession();
            local = true;
            await queueItemState(ownerId, item.id, state);
            return { ...item, ...state };
          }
          if (!demo) await cacheConfirmedItemState(ownerId, item.id, state).catch(() => undefined);
          // The state response is a summary. Keep the open reading copy intact.
          return { ...item, status: result.status };
        });
        if (!changedItem) return;
        // Refresh the original item even if the reader was left and reopened
        // while the request was in flight. Keep its latest body and other state.
        await mutateCache<ReaderItem>(["item", ownerId, item.id], (current) => current
          ? { ...current, status: changedItem.status } : current, { revalidate: false });
        const matchesLibrary = (key: unknown) => Array.isArray(key) && key[0] === "library" && key[1] === ownerId;
        if (!sameActiveContext(activeContext.current, context)) {
          // The request still changed this owner's library. Refresh mounted
          // views without taking over the reader's newer navigation intent.
          void mutateCache(matchesLibrary, undefined, { revalidate: true }).catch(() => undefined);
          return;
        }
        await mutateCache<LibraryData>(["library", ownerId, route.section, route.query], (current) => {
          if (!current) return current;
          const update = (values: ReaderItem[]) => removesFromSurface
            ? values.filter((value) => value.id !== item.id)
            : values.map((value) => value.id === item.id ? { ...value, status: changedItem.status } : value);
          return { ...current, envelope: { ...current.envelope, items: update(current.envelope.items),
            ...(current.envelope.results ? { results: update(current.envelope.results) } : {}) } };
        }, { revalidate: false });
        // Invalidate other scopes, including unmounted ones, so switching back
        // within SWR's dedupe window cannot resurrect the pre-action list.
        void mutateCache((key) => matchesLibrary(key) && Array.isArray(key)
          && (key[2] !== route.section || key[3] !== route.query), undefined, { revalidate: true }).catch(() => undefined);
        if (!sameActiveContext(activeContext.current, context)) return;
        if (removesFromSurface) navigate(destination, { replace: true });
        setNotice(restoring
          ? local ? "Restored to Library on this device · waiting to sync" : "Restored to Library"
          : local ? "Archived on this device · waiting to sync" : "Archived");
        await mutateLibrary().catch(() => undefined);
      } catch (error) {
        if (!sameActiveContext(activeContext.current, context)) return;
        const hasSpecificRecovery = error instanceof Error && error.message === "offline_mutation_queue_full"
          || error instanceof ApiError && [401, 404, 429].includes(error.status);
        setNotice(hasSpecificRecovery ? friendlyError(error)
          : restoring ? "Could not restore this reading. Try again." : "Could not archive this reading. Try again.");
      } finally {
        setChangingItemKey((current) => current === flightKey ? "" : current);
      }
    })();
    itemStateFlights.current.set(flightKey, operation);
    try { await operation; }
    finally { if (itemStateFlights.current.get(flightKey) === operation) itemStateFlights.current.delete(flightKey); }
  }

  async function deleteItem() {
    if (!selectedItem || !sessionUserId || !canUseNetwork) { setNotice(authNeedsRefresh ? "Sign in with your passkey before deleting reading." : "Delete is available online."); return; }
    const deletedItem = selectedItem;
    const ownerId = sessionUserId;
    const flightKey = `${ownerId}\u0000${deletedItem.id}`;
    if (deleteFlights.current.has(flightKey)) return deleteFlights.current.get(flightKey);
    if (itemStateFlights.current.has(flightKey)) return;
    if (!window.confirm(`Permanently delete “${deletedItem.title || "Untitled read"}”? This cannot be undone.`)) return;
    const context = { ...activeContext.current };
    const transition = archiveTransition(items, deletedItem.id);
    const operation = (async () => {
      setDeletingItemKey(flightKey);
      try {
        await finishReading("item_deleted");
        const result = await withSameOriginSessionTransitionLock(async () => {
          const preflightIsCurrent = demo
            ? sameActiveContext(activeContext.current, context)
            : await itemMutationPreflightIsCurrent(context, () => activeContext.current, ownerId);
          if (!preflightIsCurrent) return undefined;
          return api.deleteItem(deletedItem.id);
        });
        if (!result) return;
        let localCleanupFailed = false;
        try { await deleteCachedItem(ownerId, deletedItem.id); }
        catch { localCleanupFailed = true; }
        if (!sameActiveContext(activeContext.current, context)) return;
        await mutateItem(undefined, { revalidate: false });
        await mutateLibrary((current) => current ? {
          ...current,
          envelope: {
            ...current.envelope,
            items: current.envelope.items.filter((item) => item.id !== deletedItem.id),
            ...(current.envelope.results ? { results: current.envelope.results.filter((item) => item.id !== deletedItem.id) } : {}),
          },
        } : current, { revalidate: false });
        const isMobile = !window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
        navigate(isMobile || !transition.adjacentItem
          ? sectionPath(route.section, route.query)
          : readerPath(transition.adjacentItem.id, route.section, route.query), { replace: true });
        const remoteNotice = result.cleanup_pending ? "Deleted · private artifact cleanup is queued" : "Deleted permanently";
        setNotice(localCleanupFailed ? `${remoteNotice} · local offline cleanup failed; clear site data before another reader uses this device` : remoteNotice);
        await mutateLibrary().catch(() => undefined);
      } catch (error) {
        if (!sameActiveContext(activeContext.current, context)) return;
        if (error instanceof ApiError && error.code === "item_delete_protected") {
          setNotice("This reading is part of an immutable Abridged receipt and cannot be deleted.");
        } else {
          setNotice(friendlyError(error) || "Could not delete this reading.");
        }
      } finally {
        setDeletingItemKey((current) => current === flightKey ? "" : current);
      }
    })();
    deleteFlights.current.set(flightKey, operation);
    try { await operation; }
    finally { if (deleteFlights.current.get(flightKey) === operation) deleteFlights.current.delete(flightKey); }
  }

  async function keepBriefSource(source: InboxBriefSource) {
    if (!selectedBrief || !online) return;
    setBriefSourceBusyId(source.item_id);
    try {
      const brief = await api.keepInboxBriefSource(selectedBrief.id, source.item_id);
      await mutateSelectedBrief(brief, { revalidate: false });
      persistOffline(cacheInboxBrief(sessionUserId as string, brief));
      setNotice(`Kept ${source.title} in Inbox`);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not keep this source.");
    } finally {
      setBriefSourceBusyId("");
    }
  }

  async function archiveSelectedBrief() {
    if (!selectedBrief || !online || !window.confirm("Archive the covered originals? Kept and changed sources will remain in Inbox.")) return;
    setBriefArchiveBusy(true);
    try {
      const brief = await api.archiveInboxBrief(selectedBrief.id);
      await Promise.all([
        mutateSelectedBrief(brief, { revalidate: false }),
        mutateInboxBriefs(),
        mutateLibrary(),
        cacheInboxBrief(sessionUserId as string, brief),
      ]);
      navigate(briefPath(brief.id, "archive"), { replace: true });
      setNotice("Inbox Brief archived · its source receipt remains available");
    } catch (error) {
      setNotice(friendlyError(error) || "Could not archive the covered originals.");
    } finally {
      setBriefArchiveBusy(false);
    }
  }

  async function restoreBriefSource(source: InboxBriefSource) {
    if (!selectedBrief || !online) return;
    setBriefSourceBusyId(source.item_id);
    try {
      const brief = await api.restoreInboxBriefSource(selectedBrief.id, source.item_id);
      await Promise.all([mutateSelectedBrief(brief, { revalidate: false }), mutateLibrary()]);
      persistOffline(cacheInboxBrief(sessionUserId as string, brief));
      setNotice(`Restored ${source.title} to Inbox`);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not restore this source.");
    } finally {
      setBriefSourceBusyId("");
    }
  }

  async function keepPodcastSource(source: PodcastEpisodeSource) {
    if (!selectedPodcastEpisode || !online) return;
    setPodcastSourceBusyId(source.item_id);
    try {
      const episode = await api.keepPodcastSource(selectedPodcastEpisode.id, source.item_id);
      await mutateSelectedPodcastEpisode(episode, { revalidate: false });
      persistOffline(cachePodcastEpisode(sessionUserId as string, episode));
      setNotice(`Kept ${source.title} in ${source.original_status === "queue" ? "Queue" : "Inbox"}`);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not keep this source.");
    } finally {
      setPodcastSourceBusyId("");
    }
  }

  async function archivePodcastSources(itemIds: string[]) {
    if (!selectedPodcastEpisode || !online || !itemIds.length) return;
    setPodcastArchiveBusy(true);
    try {
      const episode = await api.archivePodcastSources(selectedPodcastEpisode.id, itemIds);
      await Promise.all([
        mutateSelectedPodcastEpisode(episode, { revalidate: false }),
        mutatePodcastEpisodes(),
        mutateLibrary(),
      ]);
      persistOffline(cachePodcastEpisode(sessionUserId as string, episode));
      navigate(podcastEpisodePath(episode.id, episode.status), { replace: true });
      setNotice("Episode receipt archived · changed and unselected originals stayed put");
    } catch (error) {
      setNotice(friendlyError(error) || "Could not archive the selected originals.");
    } finally {
      setPodcastArchiveBusy(false);
    }
  }

  async function restorePodcastSource(source: PodcastEpisodeSource) {
    if (!selectedPodcastEpisode || !online) return;
    setPodcastSourceBusyId(source.item_id);
    try {
      const episode = await api.restorePodcastSource(selectedPodcastEpisode.id, source.item_id);
      await Promise.all([mutateSelectedPodcastEpisode(episode, { revalidate: false }), mutateLibrary()]);
      persistOffline(cachePodcastEpisode(sessionUserId as string, episode));
      setNotice(`Restored ${source.title} to ${source.original_status === "queue" ? "Queue" : "Inbox"}`);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not restore this source.");
    } finally {
      setPodcastSourceBusyId("");
    }
  }

  async function restoreAllPodcastSources() {
    if (!selectedPodcastEpisode || !online) return;
    setPodcastRestoreAllBusy(true);
    try {
      const episode = await api.restoreAllPodcastSources(selectedPodcastEpisode.id);
      await Promise.all([mutateSelectedPodcastEpisode(episode, { revalidate: false }), mutateLibrary()]);
      persistOffline(cachePodcastEpisode(sessionUserId as string, episode));
      setNotice("Restored every eligible original from this episode");
    } catch (error) {
      setNotice(friendlyError(error) || "Could not restore these sources.");
    } finally {
      setPodcastRestoreAllBusy(false);
    }
  }

  async function generatePodcastArtwork() {
    if (!selectedPodcastEpisode || !online) return;
    setPodcastArtworkBusy(true);
    try {
      const result = await api.backfillPodcastArtwork(selectedPodcastEpisode.id);
      await mutateSelectedPodcastEpisode();
      setNotice(result.artwork_jobs_queued ? "Podcast artwork queued" : "Podcast artwork is already up to date");
    } catch (error) {
      setNotice(friendlyError(error) || "Could not generate podcast artwork.");
    } finally {
      setPodcastArtworkBusy(false);
    }
  }

  async function updateInboxBriefPreference(enabled: boolean) {
    if (!online) { setNotice("Inbox Brief preferences are available online."); return; }
    setBriefPreferenceBusy(true);
    try {
      const next = await api.updateInboxBriefPreferences(enabled);
      await mutateInboxBriefPreferences(next, { revalidate: false });
      requestedBriefDate.current = "";
      setNotice(enabled ? "Inbox Brief enabled · normalized article bodies may be processed privately" : "Inbox Brief disabled");
    } catch (error) {
      setNotice(friendlyError(error) || "Could not update Inbox Brief.");
    } finally {
      setBriefPreferenceBusy(false);
    }
  }

  async function updatePodcastPreference(enabled: boolean) {
    if (!online || !podcastCanManageFeed) { setNotice("Imprint Abridged podcast preferences are available online."); return; }
    setPodcastBusy("toggle");
    try {
      const result = await api.updatePodcastPreferences(enabled);
      await mutatePodcastPreferences(result, { revalidate: false });
      setNotice(enabled ? "Imprint Abridged podcast enabled" : "Imprint Abridged podcast disabled");
    } catch (error) {
      await clearSessionAfterPodcastAuthFailure(error, () => mutateSession(undefined, { revalidate: false }));
      setNotice(friendlyError(error) || "Could not update Imprint Abridged podcast.");
    } finally {
      setPodcastBusy("");
    }
  }

  async function copyPodcastFeedUrl() {
    if (!podcastFeedUrl) return;
    setPodcastBusy("copy");
    try {
      await navigator.clipboard.writeText(podcastFeedUrl);
      setNotice("Private podcast link copied");
    } catch {
      setNotice("Could not copy the private link. Select it and copy manually.");
    } finally {
      setPodcastBusy("");
    }
  }

  async function subscribePodcastInApplePodcasts() {
    if (!podcastFeedUrl) return;
    setPodcastBusy("apple");
    try {
      await navigator.clipboard.writeText(podcastFeedUrl);
      setNotice("Link copied · in Apple Podcasts, choose Follow a Show by URL and paste it");
    } catch {
      setNotice("Could not copy the private link. Select it and copy manually.");
    } finally {
      setPodcastBusy("");
    }
  }

  async function rotatePodcastFeed() {
    if (!online || !podcastCanManageFeed) return;
    const reissuingLegacyLink = podcastPreferences?.feed_active !== false && !podcastFeedUrl;
    const confirmation = reissuingLegacyLink
      ? "Reissue the private podcast link? Any existing podcast subscription will need the new URL."
      : "Rotate the private podcast link? Existing podcast subscriptions will stop updating.";
    if (podcastPreferences?.feed_active !== false && !window.confirm(confirmation)) return;
    setPodcastBusy("rotate");
    try {
      const result = await api.rotatePodcastFeed();
      await mutatePodcastPreferences(result, { revalidate: false });
      setNotice(reissuingLegacyLink ? "Private podcast link reissued" : "Private podcast link rotated · update your podcast app");
    } catch (error) {
      await clearSessionAfterPodcastAuthFailure(error, () => mutateSession(undefined, { revalidate: false }));
      setNotice(friendlyError(error) || "Could not rotate the private podcast link.");
    } finally {
      setPodcastBusy("");
    }
  }

  async function revokePodcastFeed() {
    if (!online || !podcastCanManageFeed || !window.confirm("Revoke the private podcast link? Existing podcast subscriptions will stop updating immediately.")) return;
    setPodcastBusy("revoke");
    try {
      const result = await api.revokePodcastFeed();
      await mutatePodcastPreferences(result, { revalidate: false });
      setNotice("Private podcast link revoked");
    } catch (error) {
      await clearSessionAfterPodcastAuthFailure(error, () => mutateSession(undefined, { revalidate: false }));
      setNotice(friendlyError(error) || "Could not revoke the private podcast link.");
    } finally {
      setPodcastBusy("");
    }
  }

  async function saveAnnotation(annotation: Annotation, note: string) {
    if (!sessionUserId) return;
    const ownerId = sessionUserId;
    const context = { ...activeContext.current };
    let local = !canUseNetwork;
    setAnnotationBusy(annotation.id);
    try {
      const updated = await withSameOriginSessionTransitionLock(async () => {
        if (!demo && !(await itemMutationPreflightIsCurrent(context, () => activeContext.current, ownerId))) return;
        if (!local) {
          try { return await api.updateAnnotation(annotation.id, note); }
          catch (error) {
            if (!isUnauthorizedApiError(error)) throw error;
            reportExpiredSession();
            local = true;
          }
        }
        await queueAnnotationNote(ownerId, annotation.item_id, annotation.id, note);
        return { ...annotation, note, updated_at: new Date().toISOString() };
      });
      if (!updated) return;
      await mutateCache<AnnotationEnvelope>(["annotations", ownerId, annotation.item_id], (current) => current
        ? { ...current, annotations: current.annotations.map((value) => value.id === updated.id ? updated : value) } : current, { revalidate: false });
      if (sameActiveContext(activeContext.current, context)) setNotice(local ? "Note saved on this device · waiting to sync" : "Note saved");
    } catch (error) {
      if (sameActiveContext(activeContext.current, context)) setNotice(friendlyError(error) || "Could not save this note.");
    } finally { setAnnotationBusy(""); }
  }

  async function unfollowSource(source: FeedSource) {
    if (!online) { setNotice("Following changes are available online."); return; }
    setFollowingBusyId(source.id);
    try {
      await api.setFollowing(source.id, false);
      await mutateFollowing((current) => current ? { ...current, feed_sources: current.feed_sources.filter((candidate) => candidate.id !== source.id) } : current, { revalidate: false });
      setNotice(`Unfollowed ${source.title || source.feed_url_host} · saved articles remain`);
    } catch (error) {
      setNotice(friendlyError(error) || "Could not unfollow this publication.");
    } finally {
      setFollowingBusyId("");
    }
  }

  function updatePreferences(value: ReaderPreferences) {
    setPreferences(value);
    savePreferences(value);
  }

  function setFocus(value: boolean) {
    const query = new URLSearchParams(location.search);
    value ? query.set("focus", "1") : query.delete("focus");
    const suffix = query.toString();
    navigate(`${location.pathname}${suffix ? `?${suffix}` : ""}`, { replace: true, state: location.state });
  }

  async function loadMore() {
    if (!canUseNetwork || !networkAllowed.current || !sessionUserId) return;
    const epoch = authEpoch.current;
    const context = { ...activeContext.current };
    const currentRequest = () => networkAllowed.current && authEpoch.current === epoch
      && context.userId === sessionUserId && sameActiveContext(activeContext.current, context);
    if (route.section === "briefings") {
      if (!briefingEnvelope?.next_cursor) return;
      const cursor = briefingEnvelope.next_cursor;
      const key = `${epoch}\u0000${context.generation}\u0000briefings\u0000${cursor}`;
      const existing = paginationFlights.current.get(key);
      if (existing) return existing;
      const operation = (async () => {
        const next = await api.briefings(cursor);
        if (!currentRequest()) return;
        await mutateBriefings((current) => {
          if (!currentRequest() || !current || current.next_cursor !== cursor) return current;
          return { ...next, briefings: appendDistinctPage(current.briefings, next.briefings) };
        }, { revalidate: false });
      })();
      paginationFlights.current.set(key, operation);
      try { await operation; }
      catch (error) { if (currentRequest()) { if (isUnauthorizedApiError(error)) setAuthNeedsRefresh(true); setNotice(friendlyError(error) || "Could not load more reading."); } }
      finally { if (paginationFlights.current.get(key) === operation) paginationFlights.current.delete(key); }
      return;
    }
    const current = libraryData?.envelope;
    if (!current?.next_cursor || route.section === "daily") return;
    const cursor = current.next_cursor;
    const scannedCount = current.search_scanned_count ?? 0;
    const key = `${epoch}\u0000${context.generation}\u0000${route.section}\u0000${route.query}\u0000${cursor}`;
    const existing = paginationFlights.current.get(key);
    if (existing) return existing;
    const controller = route.section === "search" ? new AbortController() : undefined;
    if (controller) { searchRequests.current.add(controller); setSearchPaging(true); setSearchPageError(undefined); }
    // Opening a result keeps the same search alive. Its successful page must
    // consume the returned cursor even when the reader changes the route.
    // Query/session cleanup removes ownership before any newer search starts.
    const currentPageRequest = () => controller
      ? networkAllowed.current && authEpoch.current === epoch && activeContext.current.userId === sessionUserId
        && searchRequests.current.has(controller) && !controller.signal.aborted
      : currentRequest();
    const operation = (async () => {
      const next = route.section === "search"
        ? await api.search(route.query, { cursor, minimumScannedCount: scannedCount }, controller?.signal)
        : await api.listItems(statusFor(route.section), orderFor(route.section), cursor, route.section === "starred" ? true : undefined);
      if (!currentPageRequest()) return;
      const nextItems = route.section === "starred" ? next.items.filter((item) => item.status !== "archive") : next.items;
      await mutateLibrary((latest) => {
        if (!currentPageRequest() || !latest || latest.envelope.next_cursor !== cursor || (route.section === "search" && (latest.envelope.search_scanned_count ?? 0) !== scannedCount)) return latest;
        return { envelope: withoutResultAlias({ ...next, items: appendDistinctPage(latest.envelope.items, nextItems) }), offline: false };
      }, { revalidate: false });
    })();
    paginationFlights.current.set(key, operation);
    try { await operation; }
    catch (error) {
      if (currentPageRequest()) {
        if (isUnauthorizedApiError(error)) setAuthNeedsRefresh(true);
        if (controller) setSearchPageError("Search could not continue. Retry to start a fresh search.");
        else setNotice(friendlyError(error) || "Could not load more reading.");
      }
    }
    finally {
      if (paginationFlights.current.get(key) === operation) paginationFlights.current.delete(key);
      // A reader opened from this query changes the route generation. Settle
      // its pending control as long as this query still owns the request;
      // query/session cleanup removes it before a newer search can start.
      if (controller && searchRequests.current.delete(controller)) setSearchPaging(false);
    }
  }

  async function clearOfflineDataWithNotice() {
    if (!sessionUserId) return;
    const generation = ++offlineSyncGeneration.current;
    try {
      await clearOfflineReplica(sessionUserId);
      if (generation !== offlineSyncGeneration.current) return;
      setOfflineSyncState(undefined);
      setPreferences(loadPreferences());
      setNotice("Offline reading data cleared");
    } catch {
      if (generation === offlineSyncGeneration.current) setNotice("Could not clear offline reading data.");
    }
  }

  async function handleSignOut() {
    authEpoch.current++;
    networkAllowed.current = false;
    saveDrafts.current.clear();
    noteDrafts.current.clear();
    const intent = sessionIntents.current.beginTransition();
    setSignOutBusy(true);
    setSignOutError("");
    try {
      const committed = await sessionIntents.current.transition(intent, async () => {
        await finishReading("sign_out", flushOutboxWithinSessionTransition, canUseNetwork).catch(() => undefined);
        markLocalSignOutFence();
        return signOutAuthenticatedState({
          invalidateSession: () => mutateSession(undefined, { revalidate: false }),
          clearOffline: clearOfflineData,
          broadcast: broadcastSignedOut,
          revokeRemote: online ? signOutSession : async () => undefined,
        });
      });
      if (!committed.committed) return;
      const { cleanupFailed, revocationFailed } = committed.value;
      if (!sessionIntents.current.isCurrent(intent)) return;
      const warnings = [
        revocationFailed ? "This browser is signed out, but the server session could not be revoked. Reconnect and sign in again before retrying sign-out." : "",
        cleanupFailed ? "This browser could not clear every offline copy. Clear site data before another person uses this device." : "",
      ].filter(Boolean);
      if (warnings.length) setSignInError(warnings.join(" "));
      navigate("/library", { replace: true });
    } catch {
      if (sessionIntents.current.isCurrent(intent)) setSignOutError("Could not lock this browser session. Try again.");
    } finally { setSignOutBusy(false); }
  }
}

function NoticeRegion({ notice, onDismiss }: { notice: string; onDismiss: () => void }) {
  return (
    <div className="notice-region" role="status" aria-live="polite" aria-atomic="true">
      {notice ? <button className="notice" type="button" onClick={onDismiss}>{notice}<span>Dismiss</span></button> : null}
    </div>
  );
}

async function loadLibrary(section: LibrarySection | "search", query: string, online: boolean, userId: string, onUnauthorized: () => void, signal?: AbortSignal): Promise<LibraryData> {
  const cacheKey = section === "search" ? `search:${query}` : section;
  try {
    if (!online) throw new Error("offline");
    if (section === "briefings") {
      return { envelope: { ok: true, items: [], has_more: false, next_cursor: null }, offline: !online };
    }
    if (section === "daily") {
      const context = dailyContext();
      const daily = await api.daily(context);
      const ids = selectedDailyItemIds(daily);
      const items = await loadSelectedDailyItems(ids, (chunk) => api.itemSummaries(chunk));
      const envelope: ItemListEnvelope = { ok: true, items, has_more: false, next_cursor: null };
      if (!demo) persistOffline(Promise.all([cacheDaily(userId, `${context.issueDate}:${context.bucket}`, daily), cacheList(userId, cacheKey, envelope)]));
      if (!daily.issue && daily.status === "missing") void api.requestDaily(context).catch(() => undefined);
      return { envelope, daily, offline: false };
    }
    const fetchedEnvelope = section === "search"
      ? query ? await api.search(query, undefined, signal) : emptySearchEnvelope(query)
      : await api.listItems(statusFor(section), orderFor(section), "", section === "starred" ? true : undefined);
    const pendingItems = demo ? fetchedEnvelope.items : await withPendingItemChanges(userId, fetchedEnvelope.items).catch(() => fetchedEnvelope.items);
    const visibleItems = section === "library" ? pendingItems.filter((item) => item.status === "queue" || item.status === "inbox")
      : section === "starred" ? pendingItems.filter((item) => item.is_favorite && item.status !== "archive")
      : section === "archive" ? pendingItems.filter((item) => item.status === "archive") : pendingItems;
    const envelope = withoutResultAlias({ ...fetchedEnvelope, items: visibleItems });
    signal?.throwIfAborted();
    if (!demo) persistOffline(cacheList(userId, cacheKey, envelope));
    return { envelope, offline: false };
  } catch (error) {
    signal?.throwIfAborted();
    if (isUnauthorizedApiError(error)) onUnauthorized();
    if (error instanceof ApiError && error.status < 500 && error.status !== 401) throw error;
    const envelope = section === "search"
      ? await cachedOfflineSearch(userId, query) || await cachedList(userId, cacheKey)
      : section === "briefings" || section === "daily"
        ? await cachedList(userId, cacheKey)
        : await cachedOfflineCatalog(userId, section) || await cachedList(userId, cacheKey);
    if (!envelope) throw error;
    const context = dailyContext();
    return { envelope, daily: section === "daily" ? await cachedDaily(userId, `${context.issueDate}:${context.bucket}`) : undefined, offline: true };
  }
}

function withoutResultAlias(envelope: ItemListEnvelope): ItemListEnvelope {
  const { results: _results, ...canonical } = envelope;
  return canonical;
}

function emptySearchEnvelope(query: string): SearchItemListEnvelope {
  return {
    ok: true,
    query,
    items: [],
    has_more: false,
    next_cursor: null,
    search_status: "complete",
    search_complete: true,
    search_truncated: false,
    search_truncation_reason: "",
    search_truncation_reasons: [],
    search_scanned_count: 0,
    search_scan_limit: 2_000,
  };
}

const searchTruncationExplanations: Record<SearchTruncationReason, (scanLimit: string) => string> = {
  snapshot_limit: (scanLimit) => `The safe scan cap is ${scanLimit}, so older saved items were not searched.`,
  byte_budget: () => "The safe content-reading budget was reached before all candidate text could be searched.",
  document_limit: () => "A saved item's text exceeded the safe per-item search limit, so that item and later candidates were not searched.",
  tag_limit: () => "A saved item had more tags than the safe search limit, so that item and later candidates were not searched.",
  artifact_invalid: () => "Saved-item text was unavailable or invalid, so that item and later candidates were not searched.",
};

export function searchIncompleteNotice(envelope: ItemListEnvelope | undefined, online: boolean, cached = false, pending = false): string | undefined {
  if (cached && pending && online) return "Searching your library. Showing cached reads on this device while the search runs.";
  if (cached) return online
    ? "Showing cached reads on this device. The full library search is unavailable; these results may be incomplete. Retry to search the library."
    : "Only cached reads on this device were searched. Other saved items and uncached article text may have matches. Reconnect to search the library.";
  if (envelope?.search_truncated) {
    const scannedCount = Number(envelope.search_scanned_count);
    const scannedLabel = scannedCount.toLocaleString("en-US");
    const scanLimitLabel = Number(envelope.search_scan_limit).toLocaleString("en-US");
    const explanations = (envelope.search_truncation_reasons || []).map((reason) => searchTruncationExplanations[reason](scanLimitLabel));
    const cause = explanations.length ? explanations.join(" ") : "Not every saved item could be searched.";
    return `Search stopped after checking ${scannedLabel} saved item${scannedCount === 1 ? "" : "s"}. ${cause} These results may be incomplete.`;
  }
  if (envelope?.search_status !== "in_progress") return undefined;
  if (envelope.search_pause_reason === "read_timeout") return "An article took too long to search. These results are incomplete. Continue search to retry that article; no unread items have been skipped.";
  const scannedLabel = Number(envelope.search_scanned_count).toLocaleString("en-US");
  return online
    ? `Search has checked ${scannedLabel} saved items so far. Continue search to check the remaining items.`
    : `This cached search is incomplete after ${scannedLabel} saved items. Reconnect to continue searching older items.`;
}

export function appendDistinctPage<T extends { id: string }>(current: T[], next: T[]): T[] {
  const ids = new Set(current.map((entry) => entry.id));
  if (next.some((entry) => ids.has(entry.id))) throw new ApiError(200, "invalid_response");
  return [...current, ...next];
}

async function loadBriefings(online: boolean, userId: string): Promise<BriefingEnvelope> {
  try {
    if (!online) throw new Error("offline");
    const envelope = await api.briefings();
    if (!demo) persistOffline(cacheBriefings(userId, envelope));
    return envelope;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedBriefings(userId));
  }
}

async function loadCurrentBriefing(userId: string): Promise<Briefing | null> {
  if (demo) return api.currentBriefing();
  try {
    const briefing = await api.currentBriefing();
    if (briefing) persistOffline(Promise.all([cacheBriefing(userId, briefing), cacheCurrentBriefing(userId, briefing)]));
    return briefing;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedCurrentBriefing(userId));
  }
}

async function loadBriefing(id: string, userId: string): Promise<Briefing | null> {
  if (demo) return api.briefing(id);
  try {
    const briefing = await api.briefing(id);
    persistOffline(cacheBriefing(userId, briefing));
    return briefing;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedBriefing(userId, id));
  }
}

export async function loadItem(id: string, userId: string, online = true, onUnauthorized: () => void = () => undefined): Promise<ReaderItem> {
  if (demo) return api.item(id);
  try {
    if (!online) throw new Error("offline");
    const item = await api.item(id);
    const merged = (await withPendingItemChanges(userId, [item]).catch(() => [item]))[0];
    if (merged) persistOffline(cacheArticle(userId, merged));
    return merged || item;
  } catch (error) {
    if (isUnauthorizedApiError(error)) onUnauthorized();
    return cachedValueOrThrow(error, () => cachedArticle(userId, id));
  }
}

async function loadInboxBriefs(status: "inbox" | "archive", online: boolean, userId: string): Promise<InboxBriefEnvelope> {
  try {
    if (!online) throw new Error("offline");
    const envelope = await api.inboxBriefs(status);
    if (!demo) persistOffline(cacheInboxBriefs(userId, status, envelope));
    return envelope;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedInboxBriefs(userId, status));
  }
}

async function loadInboxBrief(id: string, userId: string): Promise<InboxBrief> {
  if (demo) return api.inboxBrief(id);
  try {
    const brief = await api.inboxBrief(id);
    persistOffline(cacheInboxBrief(userId, brief));
    return brief;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedInboxBrief(userId, id));
  }
}

async function loadPodcastEpisodes(status: "inbox" | "archive", online: boolean, userId: string): Promise<PodcastEpisodeEnvelope> {
  try {
    if (!online) throw new Error("offline");
    const envelope = await api.podcastEpisodes(status);
    if (!demo) persistOffline(cachePodcastEpisodes(userId, status, envelope));
    return envelope;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedPodcastEpisodes(userId, status));
  }
}

async function loadPodcastEpisode(id: string, userId: string): Promise<PodcastEpisode> {
  if (demo) return api.podcastEpisode(id);
  try {
    const episode = await api.podcastEpisode(id);
    persistOffline(cachePodcastEpisode(userId, episode));
    return episode;
  } catch (error) {
    return cachedValueOrThrow(error, () => cachedPodcastEpisode(userId, id));
  }
}

async function loadAnnotations(itemId: string, userId: string, online = true, onUnauthorized: () => void = () => undefined): Promise<AnnotationEnvelope> {
  if (demo) return api.annotations(itemId);
  try {
    if (!online) throw new Error("offline");
    const envelope = await api.annotations(itemId);
    try {
      await cacheAnnotations(userId, itemId, envelope);
      return await cachedAnnotations(userId, itemId) || envelope;
    } catch { return envelope; }
  } catch (error) {
    if (isUnauthorizedApiError(error)) onUnauthorized();
    return cachedValueOrThrow(error, () => cachedAnnotations(userId, itemId));
  }
}

async function cachedValueOrThrow<T>(error: unknown, loadCached: () => Promise<T | undefined>): Promise<T> {
  if (error instanceof ApiError && error.status < 500 && error.status !== 401) throw error;
  let cached: T | undefined;
  try { cached = await loadCached(); }
  catch { throw error; }
  if (cached !== undefined) return cached;
  throw error;
}

export async function latestUsableSessionOrThrow(error: unknown): Promise<Session> {
  let latest: Session | undefined;
  try { latest = await cachedOfflineSession(); }
  catch { throw error; }
  if (isUsableSession(latest)) return latest;
  throw error;
}

const LOCAL_SIGN_OUT_FENCE = "imprint.local-sign-out.v1";

function localSignOutFenced(): boolean {
  try { return localStorage.getItem(LOCAL_SIGN_OUT_FENCE) === "1"; }
  catch { return false; }
}

function markLocalSignOutFence(): void {
  try { localStorage.setItem(LOCAL_SIGN_OUT_FENCE, "1"); }
  catch { /* Clearing IndexedDB still locks the local replica. */ }
}

function clearLocalSignOutFence(): void {
  try { localStorage.removeItem(LOCAL_SIGN_OUT_FENCE); }
  catch { /* Passkey authentication remains authoritative. */ }
}

function persistOffline(task: Promise<unknown>): void {
  void task.catch(() => undefined);
}

function routeStateRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

function readRetainedSearch(value: unknown): string {
  const query = routeStateRecord(value).imprintSearchValue;
  return typeof query === "string" ? query.slice(0, 512) : "";
}

function readUpdateScrollSnapshot(value: unknown): UpdateScrollSnapshot | undefined {
  const snapshot = routeStateRecord(value).imprintUpdateScroll;
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) return undefined;
  const candidate = snapshot as Record<string, unknown>;
  if (!new Set(["list", "reader", "settings"]).has(String(candidate.kind))) return undefined;
  const top = Number(candidate.top);
  if (!Number.isFinite(top) || top < 0 || top > 10_000_000) return undefined;
  const listTop = Number(candidate.listTop);
  return { kind: candidate.kind as UpdateScrollSnapshot["kind"], top: Math.round(top),
    ...(Number.isFinite(listTop) && listTop >= 0 && listTop <= 10_000_000 ? { listTop: Math.round(listTop) } : {}) };
}

function updateSurfaceScroller(kind: UpdateScrollSnapshot["kind"]): HTMLElement | null {
  const selector = kind === "settings"
    ? ".settings-page"
    : kind === "list"
      ? ".reading-list__rows"
      : ".reader-scroll";
  return document.querySelector<HTMLElement>(selector);
}

function captureUpdateSurfaceScroll(): UpdateScrollSnapshot | undefined {
  const candidates: Array<[UpdateScrollSnapshot["kind"], string]> = [
    ["settings", ".settings-page"],
    ["reader", ".reader-scroll"],
    ["list", ".reading-list__rows"],
  ];
  for (const [kind, selector] of candidates) {
    const scroller = document.querySelector<HTMLElement>(selector);
    if (scroller) {
      const list = kind === "reader" && window.matchMedia(DESKTOP_MEDIA_QUERY).matches ? updateSurfaceScroller("list") : null;
      return { kind, top: Math.max(0, Math.round(scroller.scrollTop)),
        ...(list ? { listTop: Math.max(0, Math.round(list.scrollTop)) } : {}) };
    }
  }
  return undefined;
}

function selectedDocumentTitle(route: ReturnType<typeof routeState>, settingsOpen: boolean, briefingHistoryOpen: boolean): string {
  if (settingsOpen) return "Settings";
  if (route.itemId || route.briefId || route.podcastEpisodeId || route.briefingId) return "Reading";
  if (route.section === "search") return route.query ? `Find: ${route.query}` : "Find";
  if (route.section === "briefings") return briefingHistoryOpen ? "Past editions" : "Abridged";
  if (route.section === "starred") return "Starred";
  if (route.section === "archive") return "Archive";
  return "Library";
}

export function routeState(pathname: string, search: string): { section: LibrarySection | "search"; itemId?: string; briefId?: string; podcastEpisodeId?: string; briefingId?: string; query: string } {
  // Retired routes return to reading without starting Abridge requests.
  if (/^\/(?:abridged|briefings|podcast|podcast-episodes|daily)(?:\/|$)/.test(pathname)) return { section: "library", query: "" };

  const query = new URLSearchParams(search);
  const read = pathname.match(/^\/read\/([^/]+)$/);
  if (read) {
    const rawSection = query.get("section") || "library";
    const section = rawSection === "search" || sections.includes(rawSection as LibrarySection) ? rawSection as LibrarySection | "search" : "library";
    const itemId = safeDecodeRouteSegment(read[1]);
    if (itemId !== undefined && !validCanonicalItemId(itemId)) return { section, query: query.get("q") || "" };
    return itemId === undefined ? { section, query: query.get("q") || "" } : { section, itemId, query: query.get("q") || "" };
  }
  const briefing = pathname.match(/^\/(?:abridged|briefings)\/([^/]+)$/);
  if (briefing) {
    const briefingId = safeDecodeRouteSegment(briefing[1]);
    return briefingId === undefined ? { section: "briefings", query: "" } : { section: "briefings", briefingId, query: "" };
  }
  if (pathname === "/abridged" || pathname === "/briefings") return { section: "briefings", query: "" };
  const brief = pathname.match(/^\/brief\/([^/]+)$/);
  if (brief) {
    const briefId = safeDecodeRouteSegment(brief[1]);
    return briefId === undefined ? { section: "briefings", query: "" } : { section: "briefings", briefId, query: "" };
  }
  const podcastEpisode = pathname.match(/^\/podcast\/([^/]+)$/);
  if (podcastEpisode) {
    const podcastEpisodeId = safeDecodeRouteSegment(podcastEpisode[1]);
    return podcastEpisodeId === undefined ? { section: "briefings", query: "" } : { section: "briefings", podcastEpisodeId, query: "" };
  }
  if (pathname === "/search") return { section: "search", query: query.get("q") || "" };
  if (pathname === "/settings") return { section: "library", query: "" };
  if (pathname === "/daily") return { section: "briefings", query: "" };
  if (pathname === "/library" || pathname === "/queue" || pathname === "/inbox") return { section: "library", query: "" };
  const library = pathname.match(/^\/library\/(starred|archive)$/);
  if (/^\/library\/(queue|inbox)$/.test(pathname)) return { section: "library", query: "" };
  return { section: library ? library[1] as LibrarySection : "library", query: "" };
}

function safeDecodeRouteSegment(value: string): string | undefined {
  try {
    const decoded = decodeURIComponent(value);
    return validCanonicalItemId(decoded) ? decoded : undefined;
  } catch { return undefined; }
}

function sameActiveContext(left: { generation: number; key: string; userId: string }, right: { generation: number; key: string; userId: string }): boolean {
  return left.generation === right.generation && left.key === right.key && left.userId === right.userId;
}

export async function itemMutationPreflightIsCurrent(
  expected: { generation: number; key: string; userId: string },
  current: () => { generation: number; key: string; userId: string },
  ownerId: string,
): Promise<boolean> {
  if (!sameActiveContext(current(), expected)) return false;
  if ((await cachedOfflineSession())?.user_id !== ownerId) return false;
  return sameActiveContext(current(), expected);
}

function broadcastSignedOut(): void {
  const channel = new BroadcastChannel("imprint-session");
  try { channel.postMessage("signed-out"); } finally { channel.close(); }
}

function sectionPath(section: LibrarySection | "search", query = ""): string {
  if (section === "briefings" || section === "daily") return "/abridged";
  if (section === "library" || section === "queue" || section === "inbox") return "/library";
  if (section === "search") return `/search${query ? `?q=${encodeURIComponent(query)}` : ""}`;
  return `/library/${section}`;
}

function readerPath(itemId: string, section: LibrarySection | "search", query = ""): string {
  const params = new URLSearchParams({ section });
  if (query) params.set("q", query);
  return `/read/${encodeURIComponent(itemId)}?${params.toString()}`;
}

function briefPath(briefId: string, section: LibrarySection | "search"): string {
  return `/brief/${encodeURIComponent(briefId)}?section=${section === "archive" ? "archive" : "inbox"}`;
}

function podcastEpisodePath(episodeId: string, section: LibrarySection | "search"): string {
  return `/podcast/${encodeURIComponent(episodeId)}?section=${section === "archive" ? "archive" : "inbox"}`;
}

function statusFor(section: LibrarySection): string {
  if (section === "library") return "active";
  return section === "starred" ? "all" : section;
}

function orderFor(section: LibrarySection): string {
  if (section === "library") return "library";
  return section === "inbox" ? "inbox_position" : "library_position";
}

function titleCase(value: string): string {
  if (value === "briefings") return "Abridged";
  return value.replace(/^./, (letter) => letter.toUpperCase());
}

function dailyStatus(daily?: DailyEditionEnvelope): string {
  if (daily?.status === "too_few_candidates") return "Add a few more readable saves and Daily will try again.";
  if (daily?.status === "failed") return "Daily will try again on a later refresh.";
  return "A private edition for this part of the day";
}

function friendlyError(error: unknown): string {
  if (!error) return "";
  if (error instanceof ApiError && error.status === 401) return "Your passkey session has ended. Sign in again to continue.";
  if (error instanceof Error && error.message === "offline_mutation_queue_full") return "Connect and sync before saving more offline changes. Your earlier changes are still saved.";
  if (error instanceof Error && error.message === "offline") return "This part of your library has not been opened on this device yet.";
  if (error instanceof ApiError && error.status === 404) return "This saved source is no longer available.";
  if (error instanceof ApiError && error.status === 429) return "Imprint is taking a little longer than usual. Try again in a moment.";
  if (error instanceof ApiError && (error.code === "invalid_response" || error.message === "invalid_response")) return "Imprint could not open this reading copy. Try again.";
  if (error instanceof ApiError || error instanceof Error) return "Imprint could not finish opening this view. Try again.";
  return "Something went wrong.";
}
